import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Wrench, BookOpen, CreditCard, FileText, Terminal, Settings, LogOut, Plus, Trash2, ShoppingBag, Edit3, Save, X, Eye, EyeOff, Lock, Image as ImageIcon, Palette, Type, MessageSquare, Star, Quote, Activity, RefreshCcw, Download, Upload, Award, GitBranch, BadgeCheck, Cpu, Zap, Loader2, ArrowUp, ArrowDown, User, Mail, Copy, Gift, ShieldCheck, UserCog, Globe, Megaphone, RotateCcw, Sparkles, Send, Bot, AlertTriangle, Crown, Check } from 'lucide-react';
import Logo from '../components/Logo';
import ImageInput from '../components/ImageInput';
import { useSiteConfig, DEFAULTS } from '../contexts/SiteConfigContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import RecoveryManager from './admin/RecoveryManager';
import InfraLeads from './admin/InfraLeads';
import TeamManager from './admin/TeamManager';
import TelegramBotPanel from './admin/TelegramBotPanel';
import WorksWithManager from './admin/WorksWithManager';
import AnnouncementsManager from './admin/AnnouncementsManager';
import RefundsManager from './admin/RefundsManager';
import AdminSpinPanel from './admin/AdminSpinPanel';
import TeamContentManager from './admin/TeamContentManager';

const sections = [
  { to: 'overview',    label: 'Overview',     icon: LayoutDashboard, group: 'main' },
  { to: 'branding',    label: 'Branding',     icon: Palette,         group: 'site' },
  { to: 'hero',        label: 'Hero Section', icon: Type,            group: 'site' },
  { to: 'navigation',  label: 'Navigation',   icon: GitBranch,       group: 'site' },
  { to: 'services',    label: 'Services',     icon: Wrench,          group: 'content' },
  { to: 'books',       label: 'Books',        icon: BookOpen,        group: 'content' },
  { to: 'memberships', label: 'Memberships',  icon: CreditCard,      group: 'content' },
  { to: 'comparison',  label: 'Plan Compare', icon: BadgeCheck,      group: 'content' },
  { to: 'tools',       label: 'Tools',        icon: Terminal,        group: 'content' },
  { to: 'how',         label: 'How It Works', icon: Cpu,             group: 'content' },
  { to: 'partners',    label: 'Partners',     icon: Award,           group: 'content' },
  { to: 'worksWith',   label: 'Works With',   icon: Globe,           group: 'content' },
  { to: 'teamPage',    label: 'Team Page',    icon: Crown,           group: 'content' },
  { to: 'testimonials',label: 'Testimonials', icon: Quote,           group: 'content' },
  { to: 'activity',    label: 'Live Feed',    icon: Activity,        group: 'content' },
  { to: 'stats',       label: 'Stats',        icon: Star,            group: 'content' },
  { to: 'faqs',        label: 'FAQs',         icon: MessageSquare,   group: 'content' },
  { to: 'orders',      label: 'Orders',       icon: ShoppingBag,     group: 'main' },
  { to: 'recovery',    label: 'Consultation', icon: ShieldCheck,     group: 'main' },
  { to: 'infraLeads',  label: 'Infra Leads',  icon: Terminal,        group: 'main' },
  { to: 'users',       label: 'Users',        icon: User,            group: 'main' },
  { to: 'referrals',   label: 'Referrals',    icon: Gift,            group: 'main' },
  { to: 'feed',        label: 'Feed (IG)',    icon: Activity,        group: 'main' },
  { to: 'payments',    label: 'Payments',     icon: CreditCard,      group: 'main' },
  { to: 'refunds',     label: 'Refunds',      icon: RotateCcw,       group: 'main' },
  { to: 'spin',        label: 'Spin Wheel',   icon: Sparkles,        group: 'main' },
  { to: 'coupons',     label: 'Coupons',      icon: Zap,             group: 'main' },
  { to: 'notifications', label: 'Notifications', icon: MessageSquare, group: 'main' },
  { to: 'announcements', label: 'Broadcast',    icon: Megaphone,       group: 'main' },
  { to: 'team',        label: 'Team',         icon: UserCog,         group: 'main', ownerOnly: true },
  { to: 'settings',    label: 'Settings',     icon: Settings,        group: 'main' },
];

const Sidebar = ({ active, setActive, onLogout, mode }) => (
  <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[var(--eh-border)] eh-grid-bg h-screen sticky top-0">
    <div className="p-5 border-b border-[var(--eh-border)] flex items-center gap-3">
      <Logo size={36} />
      <div><div className="eh-brand font-black tracking-widest text-sm eh-neon-soft">CONTROL</div><div className="text-[10px] eh-mono opacity-60">// {mode === 'feed_mod' ? 'feed moderator' : 'admin panel'}</div></div>
    </div>
    <nav className="p-3 flex-1 eh-scroll overflow-y-auto">
      {sections.filter(s => mode === 'owner' || (mode === 'feed_mod' && s.to === 'feed')).map(s => { const I = s.icon; return (
        <button key={s.to} onClick={()=>setActive(s.to)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded mb-1 text-[12px] eh-mono tracking-widest uppercase text-left ${active===s.to ? 'bg-[rgba(0,255,157,.1)] text-[var(--eh-green)] border border-[rgba(0,255,157,.3)]' : 'hover:bg-white/5'}`}>
          <I size={14} /> {s.label}
        </button>
      );})}
    </nav>
    <div className="p-3 border-t border-[var(--eh-border)]">
      <Link to="/" target="_blank" className="w-full flex items-center gap-2 text-sm eh-mono opacity-70 hover:opacity-100 px-3 py-2"><Activity size={14} /> view site</Link>
      <button onClick={onLogout} className="w-full flex items-center gap-2 text-sm eh-mono opacity-70 hover:opacity-100 px-3 py-2 text-left"><LogOut size={14} /> logout</button>
    </div>
  </aside>
);

const Section = ({ title, kicker, actions, children }) => (
  <div>
    <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
      <div><div className="eh-kicker mb-2">{kicker}</div><h2 className="eh-display text-2xl md:text-3xl font-black">{title}</h2></div>
      <div className="flex gap-2 flex-wrap">{actions}</div>
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, hint }) => (
  <div className="eh-panel eh-brackets p-5"><span className="br-bl" /><span className="br-br" />
    <div className="eh-mono text-xs opacity-60 tracking-widest">{label}</div>
    <div className="eh-display text-3xl font-black eh-neon mt-2">{value}</div>
    {hint && <div className="eh-mono text-[11px] opacity-60 mt-1">{hint}</div>}
  </div>
);

const Label = ({ children, hint }) => (
  <div className="eh-mono text-xs tracking-[.25em] opacity-70 mb-2 flex items-center gap-2">{children}{hint && <span className="opacity-50 normal-case">— {hint}</span>}</div>
);

const Input = (props) => <input className="eh-input" {...props} />;
const Textarea = (props) => <textarea className="eh-textarea" {...props} />;

// Edit Modal -- generic form editor for any record
const EditModal = ({ item, fields, onClose, onSave, title }) => {
  const [data, setData] = useState(item || {});
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl eh-panel eh-brackets p-5 sm:p-7 my-6" onClick={e=>e.stopPropagation()}>
        <span className="br-bl" /><span className="br-br" />
        <div className="flex items-center justify-between mb-5">
          <div><div className="eh-kicker mb-1">// {item?.id ? 'EDIT' : 'CREATE'}</div><h3 className="eh-display text-xl font-black">{title}</h3></div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto eh-scroll pr-1">
          {fields.map(f => (
            <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
              <Label hint={f.hint}>{f.label}</Label>
              {f.type === 'textarea' ? (
                <Textarea rows={f.rows || 4} value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={`> ${f.label.toLowerCase()}`} />
              ) : f.type === 'select' ? (
                <select className="eh-input" value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)}>
                  <option value="">--</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'checkbox' ? (
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!data[f.key]} onChange={e => set(f.key, e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" /> {f.label}
                </label>
              ) : f.type === 'array' ? (
                <div className="space-y-2">
                  {((data[f.key] || []).length === 0 ? [''] : data[f.key]).map((v, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={v} onChange={e => { const arr = [...(data[f.key]||[''])]; arr[idx] = e.target.value; set(f.key, arr); }} placeholder={`> item ${idx+1}`} />
                      <button type="button" onClick={()=>{ const arr=[...(data[f.key]||[])]; arr.splice(idx,1); set(f.key, arr); }} className="w-10 h-10 grid place-items-center rounded border border-[var(--eh-border)] hover:border-red-400 opacity-70"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => set(f.key, [...(data[f.key]||[]), ''])} className="eh-btn-ghost text-[11px]"><Plus size={12} /> ADD ITEM</button>
                </div>
              ) : f.key.toLowerCase().includes('cover') || f.key.toLowerCase().includes('image') || f.key === 'logoUrl' ? (
                <ImageInput value={data[f.key] || ''} onChange={(v) => set(f.key, v)} testid={`edit-${f.key}`} />
              ) : (
                <Input type={f.type === 'number' ? 'number' : 'text'} value={data[f.key] ?? ''} onChange={e => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} placeholder={`> ${f.label.toLowerCase()}`} />
              )}
              {f.key.toLowerCase().includes('cover') || f.key.toLowerCase().includes('image') || f.key === 'logoUrl' ? (
                data[f.key] && <img src={data[f.key]} alt="preview" className="mt-2 max-h-32 rounded border border-[var(--eh-border)] object-cover" onError={e => e.target.style.display='none'} />
              ) : null}
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="eh-btn-ghost text-xs">CANCEL</button>
          <button onClick={() => { onSave(data); onClose(); }} className="eh-btn-primary text-xs"><Save size={14} /> SAVE</button>
        </div>
      </div>
    </div>
  );
};

const ListManager = ({ title, kicker, items, columns, fields, onChange, idKey = 'id' }) => {
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const handleSave = (data) => {
    if (editing) {
      onChange(items.map(it => it[idKey] === editing[idKey] ? { ...it, ...data } : it));
    } else {
      const newId = data[idKey] || (idKey === 'id' ? 'x' + Date.now() : data[idKey]);
      onChange([{ ...data, [idKey]: newId }, ...items]);
    }
  };
  const remove = (it) => { onChange(items.filter(x => x[idKey] !== it[idKey])); toast.success('Removed'); };
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  return (
    <Section kicker={kicker} title={title} actions={<button onClick={()=>setShowNew(true)} className="eh-btn-primary text-xs"><Plus size={14} /> ADD NEW</button>}>
      <div className="eh-panel overflow-x-auto">
        <table className="w-full eh-mono text-sm min-w-[640px]">
          <thead><tr className="text-left border-b border-[var(--eh-border)]">{columns.map(c => <th key={c.key} className="p-3 text-xs tracking-widest opacity-70">{c.label}</th>)}<th className="p-3 w-32 text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={it[idKey]} className="border-b border-[var(--eh-border)] hover:bg-white/[.02]">
                {columns.map(c => (
                  <td key={c.key} className="p-3 align-top max-w-[260px]">
                    {c.render ? c.render(it) : <div className="truncate">{String(it[c.key] ?? '')}</div>}
                  </td>
                ))}
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={()=>move(idx, -1)} disabled={idx === 0} className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] hover:text-[var(--eh-green)] disabled:opacity-30 disabled:hover:border-[var(--eh-border)] disabled:hover:text-current" title="Move up"><ArrowUp size={12} /></button>
                    <button onClick={()=>move(idx, 1)} disabled={idx === items.length - 1} className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] hover:text-[var(--eh-green)] disabled:opacity-30 disabled:hover:border-[var(--eh-border)] disabled:hover:text-current" title="Move down"><ArrowDown size={12} /></button>
                    <button onClick={()=>setEditing(it)} className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] hover:text-[var(--eh-green)]"><Edit3 size={12} /></button>
                    <button onClick={()=>remove(it)} className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-red-400 hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length+1} className="p-6 text-center opacity-60">No entries yet. Click ADD NEW.</td></tr>}
          </tbody>
        </table>
      </div>
      {(editing || showNew) && <EditModal title={title} item={editing} fields={fields} onClose={() => { setEditing(null); setShowNew(false); }} onSave={handleSave} />}
    </Section>
  );
};

const Branding = () => {
  const { config, update } = useSiteConfig();
  const s = config.site;
  return (
    <Section kicker="// IDENTITY" title="BRANDING & IDENTITY">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="eh-panel p-5 space-y-4">
          <Label hint="brand name shown everywhere">SITE NAME</Label>
          <Input value={s.name} onChange={e => update('site.name', e.target.value)} />
          <Label hint="script tagline below brand">TAGLINE</Label>
          <Input value={s.tagline} onChange={e => update('site.tagline', e.target.value)} />
          <Label hint="paragraph under hero">DESCRIPTION</Label>
          <Textarea rows={3} value={s.description} onChange={e => update('site.description', e.target.value)} />
          <Label hint="text that scrolls on top">MARQUEE TEXT</Label>
          <Input value={s.marquee} onChange={e => update('site.marquee', e.target.value)} />
          <Label hint="version shown in boot/footer">VERSION TAG</Label>
          <Input value={s.version} onChange={e => update('site.version', e.target.value)} />
        </div>
        <div className="eh-panel p-5 space-y-4">
          <Label hint="upload from device or paste URL">LOGO IMAGE</Label>
          <ImageInput value={s.logoUrl || ''} onChange={(v) => update('site.logoUrl', v)} testid="branding-logo" />
          {s.logoUrl && <div className="flex justify-center pt-1"><div className="w-28 h-28 rounded-full overflow-hidden" style={{ boxShadow: '0 0 0 2px var(--eh-green), 0 0 18px rgba(0,255,157,.4)' }}><img src={s.logoUrl} alt="logo" className="w-full h-full object-cover" /></div></div>}
          <Label hint="neon green primary color">BRAND COLOR</Label>
          <div className="flex gap-2 items-center"><input type="color" value={s.brandColor || '#00ff9d'} onChange={e => update('site.brandColor', e.target.value)} className="w-12 h-12 rounded cursor-pointer bg-transparent border border-[var(--eh-border)]" /><Input value={s.brandColor || '#00ff9d'} onChange={e => update('site.brandColor', e.target.value)} /></div>
          <div className="flex gap-2 flex-wrap">
            {['#00ff9d','#ff2a3a','#4de0ff','#ffcc00','#a855f7','#22c55e','#f97316'].map(c => (
              <button key={c} onClick={() => update('site.brandColor', c)} className="w-8 h-8 rounded border" style={{ background: c, borderColor: 'var(--eh-border)' }} />
            ))}
          </div>
          <Label hint="telegram link">TELEGRAM URL</Label>
          <Input value={s.telegram} onChange={e => update('site.telegram', e.target.value)} />
          <Label>SUPPORT EMAIL</Label>
          <Input value={s.email} onChange={e => update('site.email', e.target.value)} />
        </div>
      </div>
      <div className="eh-mono text-xs opacity-60 mt-4">// Changes save automatically and apply to the live site.</div>
    </Section>
  );
};

const HeroEditor = () => {
  const { config, update } = useSiteConfig();
  const h = config.hero;
  return (
    <Section kicker="// LANDING" title="HERO SECTION">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="eh-panel p-5 space-y-4">
          <Label>SYSTEM KICKER</Label>
          <Input value={h.kicker} onChange={e => update('hero.kicker', e.target.value)} />
          <Label>WELCOME LINE</Label>
          <Input value={h.welcome} onChange={e => update('hero.welcome', e.target.value)} />
          <Label>PRIMARY BUTTON</Label>
          <div className="grid grid-cols-2 gap-2"><Input value={h.primaryCta} onChange={e=>update('hero.primaryCta', e.target.value)} placeholder="label" /><Input value={h.primaryCtaTo} onChange={e=>update('hero.primaryCtaTo', e.target.value)} placeholder="/services" /></div>
          <Label>GHOST BUTTON</Label>
          <div className="grid grid-cols-2 gap-2"><Input value={h.ghostCta} onChange={e=>update('hero.ghostCta', e.target.value)} placeholder="label" /><Input value={h.ghostCtaTo} onChange={e=>update('hero.ghostCtaTo', e.target.value)} placeholder="/tools" /></div>
        </div>
        <div className="eh-panel p-5 space-y-3">
          <Label hint="terminal-style typing lines">TYPEWRITER LINES</Label>
          {(h.typewriterLines || []).map((line, i) => (
            <div key={i} className="flex gap-2">
              <Input value={line} onChange={e => { const arr=[...h.typewriterLines]; arr[i] = e.target.value; update('hero.typewriterLines', arr); }} />
              <button onClick={() => update('hero.typewriterLines', h.typewriterLines.filter((_,k)=>k!==i))} className="w-10 h-10 grid place-items-center rounded border border-[var(--eh-border)] hover:border-red-400 opacity-70"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => update('hero.typewriterLines', [...(h.typewriterLines||[]), 'new line...'])} className="eh-btn-ghost text-[11px]"><Plus size={12} /> ADD LINE</button>
        </div>
      </div>
    </Section>
  );
};

const NavigationEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// MENU"
      title="NAVIGATION ITEMS"
      items={config.nav.map((n, i) => ({ id: 'nav-' + i, ...n }))}
      columns={[
        { key: 'label', label: 'LABEL' },
        { key: 'to',    label: 'PATH' },
      ]}
      fields={[
        { key: 'label', label: 'Label', full: true },
        { key: 'to',    label: 'Path', hint: '/services, /books, etc.', full: true },
      ]}
      onChange={(arr) => setList('nav', arr.map(({ id, ...r }) => r))}
    />
  );
};

const ServicesEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// CATALOG"
      title="MANAGE SERVICES"
      items={config.services}
      columns={[
        { key: 'name',  label: 'NAME' },
        { key: 'price', label: 'PRICE', render: it => <span className="eh-neon-soft">${it.price}</span> },
        { key: 'delivery', label: 'DELIVERY' },
        { key: 'tag', label: 'TAG' },
      ]}
      fields={[
        { key: 'name', label: 'Name', full: true },
        { key: 'short', label: 'Short Description', full: true, type: 'textarea', rows: 2 },
        { key: 'long', label: 'Long Description', full: true, type: 'textarea', rows: 5 },
        { key: 'price', label: 'Price (USD)', type: 'number' },
        { key: 'delivery', label: 'Delivery Time', hint: 'e.g. 24-72 hours' },
        { key: 'guarantee', label: 'Guarantee' },
        { key: 'tag', label: 'Tag', hint: 'POPULAR / NEW / HOT' },
        { key: 'icon', label: 'Icon', type: 'select', options: ['youtube','instagram','send','facebook','music','shield'] },
      ]}
      onChange={(arr) => setList('services', arr)}
    />
  );
};

const BooksEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// LIBRARY"
      title="MANAGE BOOKS"
      items={config.books}
      columns={[
        { key: 'title',  label: 'TITLE' },
        { key: 'author', label: 'AUTHOR' },
        { key: 'price',  label: 'PRICE', render: it => <span className="eh-neon-soft">${it.price}</span> },
        { key: 'level',  label: 'LEVEL' },
      ]}
      fields={[
        { key: 'title', label: 'Title', full: true },
        { key: 'author', label: 'Author' },
        { key: 'price', label: 'Price (USD)', type: 'number' },
        { key: 'pages', label: 'Pages', type: 'number' },
        { key: 'level', label: 'Level', type: 'select', options: ['Beginner','Intermediate','Advanced','All'] },
        { key: 'tag',   label: 'Tag', hint: 'Bestseller / New / Hot / Pro' },
        { key: 'cover', label: 'Cover Image URL', full: true },
      ]}
      onChange={(arr) => setList('books', arr)}
    />
  );
};

const MembershipsEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// TIERS"
      title="MANAGE MEMBERSHIPS"
      items={config.memberships}
      columns={[
        { key: 'name',   label: 'TIER' },
        { key: 'price',  label: 'PRICE', render: it => <span className="eh-neon-soft">${it.price}/{it.period}</span> },
        { key: 'color',  label: 'COLOR' },
        { key: 'popular',label: 'POPULAR', render: it => it.popular ? 'YES' : '-' },
      ]}
      fields={[
        { key: 'name', label: 'Tier Name', hint: 'ROOKIE / OPERATOR / ELITE' },
        { key: 'price', label: 'Price', type: 'number' },
        { key: 'period', label: 'Period', hint: 'mo, yr' },
        { key: 'color', label: 'Color', type: 'select', options: ['cyan','green','red'] },
        { key: 'popular', label: 'Mark as Most Popular', type: 'checkbox' },
        { key: 'perks', label: 'Perks (one per line)', type: 'array', full: true },
      ]}
      onChange={(arr) => setList('memberships', arr)}
    />
  );
};

const BlogsEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// CONTENT"
      title="MANAGE BLOGS"
      items={config.blogs}
      columns={[
        { key: 'title', label: 'TITLE' },
        { key: 'tag', label: 'TAG' },
        { key: 'date', label: 'DATE' },
      ]}
      fields={[
        { key: 'title', label: 'Title', full: true },
        { key: 'tag', label: 'Tag', hint: 'ARTICLE / GUIDE / STORY / LAB' },
        { key: 'date', label: 'Date', hint: 'YYYY-MM-DD' },
        { key: 'excerpt', label: 'Excerpt', full: true, type: 'textarea', rows: 3 },
        { key: 'cover', label: 'Cover Image URL', full: true },
      ]}
      onChange={(arr) => setList('blogs', arr)}
    />
  );
};

const ToolsEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// ARSENAL"
      title="MANAGE TOOLS"
      items={config.tools}
      columns={[
        { key: 'name', label: 'NAME' },
        { key: 'category', label: 'CATEGORY' },
        { key: 'size', label: 'SIZE' },
        { key: 'downloads', label: 'DOWNLOADS' },
      ]}
      fields={[
        { key: 'name', label: 'Name', full: true },
        { key: 'category', label: 'Category', hint: 'Termux / Recon / OSINT' },
        { key: 'size', label: 'File Size', hint: 'e.g. 4.2 MB' },
        { key: 'downloads', label: 'Downloads (count)', type: 'number' },
        { key: 'desc', label: 'Description', full: true, type: 'textarea', rows: 3 },
      ]}
      onChange={(arr) => setList('tools', arr)}
    />
  );
};

const HowEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// PROTOCOL"
      title="HOW IT WORKS STEPS"
      items={config.howSteps.map((s, i) => ({ id: 'how-' + i, ...s }))}
      columns={[
        { key: 'n', label: 'STEP' },
        { key: 't', label: 'TITLE' },
        { key: 'icon', label: 'ICON' },
      ]}
      fields={[
        { key: 'n', label: 'Step Number', hint: '01, 02, ...' },
        { key: 't', label: 'Title' },
        { key: 'icon', label: 'Icon', type: 'select', options: ['MessageSquare','Lock','Cpu','BadgeCheck','Shield','Zap','Send','Star'] },
        { key: 'd', label: 'Description', full: true, type: 'textarea', rows: 3 },
      ]}
      onChange={(arr) => setList('howSteps', arr.map(({ id, ...r }) => r))}
    />
  );
};

const PartnersEditor = () => {
  const { config, setList } = useSiteConfig();
  const [val, setVal] = useState('');
  return (
    <Section kicker="// AS_SEEN_ON" title="PARTNER LOGOS / BRANDS" actions={<button onClick={()=>setList('partners', DEFAULTS.partners)} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> RESET</button>}>
      <div className="eh-panel p-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {config.partners.map((p, i) => (
            <div key={p+i} className="flex items-center gap-2 px-3 py-1.5 rounded text-sm eh-mono" style={{ background: 'rgba(0,255,157,.08)', border: '1px solid rgba(0,255,157,.25)' }}>
              {p}
              <button onClick={() => setList('partners', config.partners.filter((_,k)=>k!==i))} className="opacity-60 hover:opacity-100 hover:text-red-400"><X size={12} /></button>
            </div>
          ))}
        </div>
        <form onSubmit={e=>{e.preventDefault(); if(val.trim()){ setList('partners', [...config.partners, val.trim()]); setVal(''); }}} className="flex gap-2">
          <Input value={val} onChange={e=>setVal(e.target.value)} placeholder="> new brand name" />
          <button className="eh-btn-primary text-xs"><Plus size={12} /> ADD</button>
        </form>
      </div>
    </Section>
  );
};

const TestimonialsEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// FEEDBACK"
      title="MANAGE TESTIMONIALS"
      items={config.testimonials.map((t,i)=>({ id: 'tm-'+i, ...t }))}
      columns={[{ key: 'name', label: 'NAME' }, { key: 'role', label: 'ROLE' }, { key: 'text', label: 'QUOTE' }]}
      fields={[
        { key: 'name', label: 'Name' },
        { key: 'role', label: 'Role / Title' },
        { key: 'text', label: 'Quote', full: true, type: 'textarea', rows: 4 },
      ]}
      onChange={(arr) => setList('testimonials', arr.map(({ id, ...r }) => r))}
    />
  );
};

const ActivityEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// LIVE_FEED"
      title="LIVE ORDER FEED ENTRIES"
      items={config.activity}
      columns={[
        { key: 'user', label: 'USER' },
        { key: 'service', label: 'SERVICE' },
        { key: 'location', label: 'LOCATION' },
        { key: 'amount', label: 'AMOUNT', render: it => <span className="eh-neon-soft">${it.amount}</span> },
      ]}
      fields={[
        { key: 'user', label: 'User handle', hint: '@username' },
        { key: 'service', label: 'Service' },
        { key: 'location', label: 'Location' },
        { key: 'ago', label: 'Time ago', hint: '14s ago / 1m ago' },
        { key: 'amount', label: 'Amount (USD)', type: 'number' },
      ]}
      onChange={(arr) => setList('activity', arr)}
    />
  );
};

const StatsEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// METRICS"
      title="HEADLINE STATS"
      items={config.stats.map((s,i)=>({ id: 'st-'+i, ...s }))}
      columns={[{ key: 'label', label: 'LABEL' }, { key: 'value', label: 'VALUE' }]}
      fields={[
        { key: 'label', label: 'Label' },
        { key: 'value', label: 'Value', hint: 'e.g. 12,400+ or 4.9/5' },
      ]}
      onChange={(arr) => setList('stats', arr.map(({ id, ...r }) => r))}
    />
  );
};

const FAQEditor = () => {
  const { config, setList } = useSiteConfig();
  return (
    <ListManager
      kicker="// KNOWLEDGE"
      title="MANAGE FAQS"
      items={config.faqs.map((f,i)=>({ id: 'faq-'+i, ...f }))}
      columns={[{ key: 'q', label: 'QUESTION' }]}
      fields={[
        { key: 'q', label: 'Question', full: true },
        { key: 'a', label: 'Answer', full: true, type: 'textarea', rows: 5 },
      ]}
      onChange={(arr) => setList('faqs', arr.map(({ id, ...r }) => r))}
    />
  );
};

const ComparisonEditor = () => {
  const { config, setList } = useSiteConfig();
  const update = (i, key, val) => setList('comparison', config.comparison.map((r, k) => k === i ? { ...r, [key]: val } : r));
  return (
    <Section kicker="// MATRIX" title="PLAN COMPARISON FEATURES" actions={<button onClick={() => setList('comparison', [...config.comparison, { f: 'New feature', r: false, o: false, e: false }])} className="eh-btn-primary text-xs"><Plus size={12} /> ADD ROW</button>}>
      <div className="eh-panel overflow-x-auto">
        <table className="w-full eh-mono text-sm min-w-[520px]">
          <thead><tr className="text-left border-b border-[var(--eh-border)]"><th className="p-3 text-xs tracking-widest opacity-70">FEATURE</th><th className="p-3 text-center text-xs">ROOKIE</th><th className="p-3 text-center text-xs">OPERATOR</th><th className="p-3 text-center text-xs">ELITE</th><th className="p-3 w-12"></th></tr></thead>
          <tbody>
            {config.comparison.map((r, i) => (
              <tr key={i} className="border-b border-[var(--eh-border)]">
                <td className="p-2"><Input value={r.f} onChange={e=>update(i,'f',e.target.value)} /></td>
                {['r','o','e'].map(k => (<td key={k} className="p-2 text-center"><input type="checkbox" checked={!!r[k]} onChange={e=>update(i,k,e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" /></td>))}
                <td className="p-2 text-right"><button onClick={()=>setList('comparison', config.comparison.filter((_,k)=>k!==i))} className="opacity-60 hover:text-red-400"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const ORDER_STATUSES = [
  { v: 'received',        label: 'RECEIVED',        cls: 'text-[#4de0ff]' },
  { v: 'payment_review',  label: 'PAYMENT REVIEW',  cls: 'text-[#ffd34d]' },
  { v: 'verified',        label: 'VERIFIED',        cls: 'text-[var(--eh-green)]' },
  { v: 'in-progress',     label: 'IN PROGRESS',     cls: 'text-[#ffd34d]' },
  { v: 'delivered',       label: 'DELIVERED',       cls: 'text-[var(--eh-green)]' },
  { v: 'paid',            label: 'PAID',            cls: 'text-[var(--eh-green)]' },
];

const OrderField = ({ l, v, link, mono }) => (
  <div className="min-w-0">
    <div className="eh-mono text-[10px] opacity-60 tracking-widest">{l}</div>
    {link ? (
      <a href={link} target="_blank" rel="noreferrer" className={`text-sm text-[var(--eh-green)] hover:underline break-all ${mono ? 'eh-mono' : ''}`}>{v || '—'}</a>
    ) : (
      <div className={`text-sm break-all ${mono ? 'eh-mono' : ''}`}>{v || '—'}</div>
    )}
  </div>
);

const SetOrderQuotePanel = ({ order, onUpdated }) => {
  const hasQuote = Number(order.payment_amount || order.amount || 0) > 0;
  const [amount, setAmount] = useState(Number(order.payment_amount || order.amount || 0) || '');
  const [currency, setCurrency] = useState(order.payment_currency || order.currency || 'INR');
  const [note, setNote] = useState(order.notes || '');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!hasQuote);

  useEffect(() => {
    setAmount(Number(order.payment_amount || order.amount || 0) || '');
    setCurrency(order.payment_currency || order.currency || 'INR');
    setNote(order.notes || '');
    setEditing(!(Number(order.payment_amount || order.amount || 0) > 0));
  }, [order.id, order.payment_amount, order.amount, order.payment_currency, order.currency, order.notes]);

  const send = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      const res = await api.setOrderQuote(order.id, { amount: Number(amount), currency, note });
      toast.success(hasQuote ? 'Quote updated · customer pinged' : 'Quote sent · customer notified', { description: `Pay link: /track?id=${order.id}&pay=1` });
      setEditing(false);
      onUpdated?.(res.order);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const sym = { INR: '₹', USD: '$', EUR: '€', GBP: '£' }[currency] || '';

  return (
    <div className="eh-panel p-4 bg-[rgba(0,255,157,.04)] border border-[rgba(0,255,157,.25)]" data-testid="order-quote-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="eh-mono text-[10px] opacity-70 tracking-widest flex items-center gap-2"><CreditCard size={11} className="text-[var(--eh-green)]" /> // QUOTE / PAYMENT REQUEST</div>
        {hasQuote && <a href={`/track?id=${order.id}&pay=1`} target="_blank" rel="noreferrer" className="eh-mono text-[10px] text-[var(--eh-green)] hover:underline flex items-center gap-1">↗ PAY LINK</a>}
      </div>
      {hasQuote && !editing ? (
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <div className="eh-display text-2xl font-black eh-neon">{sym}{Number(order.payment_amount || order.amount || 0).toLocaleString('en-IN')}</div>
            <div className="eh-mono text-[10px] opacity-60">{currency}</div>
          </div>
          {order.notes && <div className="eh-mono text-[11px] opacity-70 leading-5">{order.notes}</div>}
          {order.quote_sent_at && <div className="eh-mono text-[10px] opacity-50">// SENT {new Date(order.quote_sent_at).toLocaleString()}</div>}
          <button onClick={() => setEditing(true)} data-testid="order-quote-edit-btn" className="text-[10px] eh-mono tracking-widest px-3 py-1.5 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] flex items-center gap-1.5 mt-1"><Edit3 size={11} /> UPDATE & RESEND</button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="eh-mono text-[10px] opacity-60 tracking-widest mb-1 block">AMOUNT</label>
              <input type="number" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 1499" className="eh-input text-sm w-full" data-testid="order-quote-amount" />
            </div>
            <div>
              <label className="eh-mono text-[10px] opacity-60 tracking-widest mb-1 block">CURRENCY</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)} className="eh-input text-sm w-full">
                <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
              </select>
            </div>
          </div>
          <div>
            <label className="eh-mono text-[10px] opacity-60 tracking-widest mb-1 block">NOTE FOR CUSTOMER (optional)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="e.g. 50% upfront, balance on delivery" className="eh-input text-sm w-full resize-none" data-testid="order-quote-note" />
          </div>
          <div className="flex gap-2">
            <button onClick={send} disabled={busy} data-testid="order-quote-send-btn" className="eh-btn-primary text-xs flex-1 justify-center inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {busy ? 'SENDING…' : hasQuote ? 'UPDATE & RESEND' : 'SEND QUOTE TO CUSTOMER'}
            </button>
            {hasQuote && <button onClick={() => setEditing(false)} className="text-[10px] eh-mono tracking-widest px-3 py-1.5 rounded border border-[var(--eh-border)]">CANCEL</button>}
          </div>
          <div className="eh-mono text-[10px] opacity-50 leading-5">Customer gets an email with a one-tap pay link. If they have Telegram linked, they get a DM too.</div>
        </div>
      )}
    </div>
  );
};

// (Compliance pivot Feb-2026) SMM auto-placement removed. This panel now
// renders read-only historical fields for legacy orders that still carry
// smm_* metadata — no place/poll API calls are made.
const SmmPlacementPanel = ({ order }) => {
  const placed = !!order.smm_panel_order_id;
  const errored = !!order.smm_error && !placed;
  const status = (order.smm_status || '').trim();
  const remains = order.smm_remains;
  const startCount = order.smm_start_count;
  if (!placed && !errored && !order.smm_last_attempt_at) return null;
  return (
    <div className="eh-panel p-4" style={{ borderColor: errored ? '#ff314866' : 'rgba(255,255,255,.15)', background: errored ? 'rgba(255,49,72,.05)' : 'rgba(255,255,255,.02)' }} data-testid="smm-placement-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="eh-mono text-[10px] opacity-60 tracking-widest flex items-center gap-2"><Bot size={11} /> // LEGACY PANEL DATA (read-only)</div>
      </div>
      {placed ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><div className="eh-mono text-[9px] opacity-60 tracking-widest">PANEL ORDER</div><div className="eh-mono text-xs opacity-80">#{order.smm_panel_order_id}</div></div>
          <div><div className="eh-mono text-[9px] opacity-60 tracking-widest">STATUS</div><div className="eh-mono text-xs opacity-80">{status || '—'}</div></div>
          <div><div className="eh-mono text-[9px] opacity-60 tracking-widest">REMAINS</div><div className="eh-mono text-xs opacity-80">{remains !== undefined ? remains : '—'}</div></div>
          <div><div className="eh-mono text-[9px] opacity-60 tracking-widest">START COUNT</div><div className="eh-mono text-xs opacity-80">{startCount !== undefined ? startCount : '—'}</div></div>
          {order.smm_charge_inr && <div className="col-span-2 sm:col-span-4 eh-mono text-[10px] opacity-50 mt-1">Historical cost: ₹{Number(order.smm_charge_inr).toLocaleString('en-IN')} · {order.smm_placed_at ? new Date(order.smm_placed_at).toLocaleString() : '—'}</div>}
        </div>
      ) : (
        <div className="eh-mono text-[11px] opacity-60 flex items-start gap-2"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{order.smm_error || 'Legacy placement failed'}</div>
      )}
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const reload = async () => {
    setLoading(true);
    try { setOrders(await api.listOrders()); } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const clearAll = async () => {
    if (!window.confirm('Delete ALL orders permanently?')) return;
    try { await api.clearOrders(); setOrders([]); setSelected(null); toast.success('Orders cleared'); } catch (e) { toast.error(e.message); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.updateOrder(id, status);
      const fresh = await api.getOrder(id);
      setOrders(prev => prev.map(o => o.id === id ? fresh : o));
      if (selected?.id === id) setSelected(fresh);
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(e.message); }
  };

  const filtered = useMemo(() => {
    let rows = filter === 'all' ? orders : orders.filter(o => o.status === filter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter(o => (o.id || '').toLowerCase().includes(q) || (o.email || '').toLowerCase().includes(q) || (o.name || '').toLowerCase().includes(q) || (o.serviceName || '').toLowerCase().includes(q));
    }
    return rows;
  }, [orders, filter, query]);

  // Top stat tiles — quick at-a-glance for admin (matches Recovery dashboard polish)
  const stats = useMemo(() => {
    const total = orders.length;
    const awaitingQuote = orders.filter(o => !(Number(o.payment_amount || o.amount || 0) > 0)).length;
    const paymentReview = orders.filter(o => o.status === 'payment_review').length;
    const verifiedSum = orders.filter(o => ['verified', 'in-progress', 'delivered', 'paid'].includes(o.status)).reduce((s, o) => s + Number(o.payment_amount || o.amount || 0), 0);
    return { total, awaitingQuote, paymentReview, verifiedSum };
  }, [orders]);

  return (
    <Section kicker="// INCOMING" title="ORDERS INBOX" actions={<><button onClick={reload} className="eh-btn-ghost text-xs" data-testid="orders-refresh-btn"><RefreshCcw size={12} /> REFRESH</button><button onClick={clearAll} className="eh-btn-ghost text-xs" data-testid="orders-clear-btn"><Trash2 size={12} /> CLEAR ALL</button></>}>
      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="eh-panel p-4"><div className="eh-mono text-[10px] opacity-60">TOTAL ORDERS</div><div className="eh-display text-2xl font-black eh-neon">{stats.total}</div></div>
        <div className="eh-panel p-4" style={{ borderColor: stats.awaitingQuote ? 'rgba(255,211,77,.4)' : undefined }}><div className="eh-mono text-[10px] opacity-60">AWAITING QUOTE</div><div className="eh-display text-2xl font-black" style={{ color: stats.awaitingQuote ? '#ffd34d' : undefined }}>{stats.awaitingQuote}</div><div className="eh-mono text-[10px] opacity-50 mt-0.5">needs price</div></div>
        <div className="eh-panel p-4" style={{ borderColor: stats.paymentReview ? 'rgba(77,224,255,.4)' : undefined }}><div className="eh-mono text-[10px] opacity-60">PAYMENT REVIEW</div><div className="eh-display text-2xl font-black" style={{ color: stats.paymentReview ? '#4de0ff' : undefined }}>{stats.paymentReview}</div><div className="eh-mono text-[10px] opacity-50 mt-0.5">verify proof</div></div>
        <div className="eh-panel p-4"><div className="eh-mono text-[10px] opacity-60">REVENUE LOCKED</div><div className="eh-display text-2xl font-black eh-neon">₹{stats.verifiedSum.toLocaleString('en-IN')}</div><div className="eh-mono text-[10px] opacity-50 mt-0.5">verified+</div></div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={() => setFilter('all')} data-testid="orders-filter-all" className={`text-[10px] eh-mono tracking-widest px-3 py-1.5 rounded border ${filter === 'all' ? 'border-[var(--eh-green)] text-[var(--eh-green)] bg-[rgba(0,255,157,.08)]' : 'border-[var(--eh-border)]'}`}>ALL ({orders.length})</button>
        {ORDER_STATUSES.map(s => {
          const n = orders.filter(o => o.status === s.v).length;
          if (n === 0 && filter !== s.v) return null;
          return <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`orders-filter-${s.v}`} className={`text-[10px] eh-mono tracking-widest px-3 py-1.5 rounded border ${filter === s.v ? 'border-[var(--eh-green)] text-[var(--eh-green)] bg-[rgba(0,255,157,.08)]' : 'border-[var(--eh-border)]'}`}>{s.label} ({n})</button>;
        })}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="> search by ID, email, name, service…" className="eh-input text-sm w-full" data-testid="orders-search" />
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin inline-block" /></div>
      ) : filtered.length === 0 ? (
        <div className="eh-panel p-10 text-center eh-mono text-xs opacity-60">{orders.length === 0 ? 'Inbox empty. Place a test order from the storefront.' : 'No orders match this view.'}</div>
      ) : (
        <div className="eh-panel overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[rgba(255,255,255,.03)] eh-mono text-[10px] tracking-widest uppercase opacity-70">
              <tr>
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3">Service</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Quote</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">—</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => {
                const st = ORDER_STATUSES.find(s => s.v === o.status) || { label: (o.status || '—').toUpperCase(), cls: 'opacity-70' };
                const amt = Number(o.payment_amount || o.amount || 0);
                return (
                  <tr key={o.id} className="border-t border-[var(--eh-border)] hover:bg-white/5 cursor-pointer" onClick={() => setSelected(o)} data-testid={`order-row-${o.id}`}>
                    <td className="p-3"><div className="eh-mono text-xs eh-neon-soft">{o.id}</div><div className="text-[10px] opacity-50">{new Date(o.createdAt).toLocaleString()}</div></td>
                    <td className="p-3"><div className="text-sm">{o.serviceName || '—'}</div>{o.size && <div className="eh-mono text-[10px] opacity-60">qty {o.size}</div>}</td>
                    <td className="p-3 text-xs"><div>{o.name || '—'}</div><div className="opacity-60">{o.email || o.userEmail || '—'}</div></td>
                    <td className="p-3">{amt > 0 ? <span className="eh-mono eh-neon">₹{amt.toLocaleString('en-IN')}</span> : <span className="eh-mono text-[#ffd34d] text-[10px] tracking-widest">// AWAITING</span>}</td>
                    <td className="p-3"><span className={`eh-mono text-[10px] font-bold tracking-widest ${st.cls}`}>{st.label}</span></td>
                    <td className="p-3 text-right"><button onClick={(e) => { e.stopPropagation(); setSelected(o); }} className="text-xs underline opacity-80 hover:opacity-100">Open</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="order-detail-modal">
          <div onClick={e => e.stopPropagation()} className="eh-panel max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-[#0d1115]">
            <div className="flex items-center justify-between p-4 border-b border-[var(--eh-border)] sticky top-0 bg-[#0d1115] z-10">
              <div>
                <div className="eh-mono text-[10px] opacity-60">ORDER</div>
                <div className="eh-neon eh-mono font-bold">{selected.id}</div>
              </div>
              <button onClick={() => setSelected(null)} aria-label="close" data-testid="order-detail-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <OrderField l="Service" v={selected.serviceName || selected.service} />
                <OrderField l="Size / Qty" v={selected.size} />
                <OrderField l="Name" v={selected.name} />
                <OrderField l="Email" v={selected.email || selected.userEmail} />
                <OrderField l="Telegram" v={selected.tg} link={selected.tg ? `https://t.me/${(selected.tg || '').replace(/^@/, '')}` : null} />
                <OrderField l="Created" v={new Date(selected.createdAt).toLocaleString()} />
                <div className="col-span-2">
                  <OrderField l="Target / URL" v={selected.target} link={selected.target && /^https?:\/\//.test(selected.target) ? selected.target : null} mono />
                </div>
                {selected.case_id && <div className="col-span-2"><OrderField l="Linked recovery case" v={selected.case_id} link={`/track?id=${selected.case_id}`} mono /></div>}
              </div>
              {selected.notes && (
                <div>
                  <div className="eh-mono text-[10px] opacity-60 mb-1">NOTES</div>
                  <div className="eh-panel p-3 text-sm whitespace-pre-wrap leading-6">{selected.notes}</div>
                </div>
              )}

              {/* Quote / Payment Request */}
              <SetOrderQuotePanel order={selected} onUpdated={(o) => { setSelected(o); setOrders(prev => prev.map(x => x.id === o.id ? o : x)); }} />

              {/* Payment proof (if customer submitted) */}
              {(selected.payment_method || selected.payment_proof_url || selected.payment_tx_reference) && (
                <div className="eh-panel p-4 border border-[var(--eh-border)]">
                  <div className="eh-mono text-[10px] opacity-70 tracking-widest mb-2 flex items-center gap-2"><BadgeCheck size={11} className="text-[var(--eh-green)]" /> // CUSTOMER PAYMENT</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <OrderField l="Method" v={(selected.payment_method || '—').toUpperCase()} mono />
                    <OrderField l="Amount paid" v={selected.payment_amount ? `${(selected.payment_currency || 'INR')} ${Number(selected.payment_amount).toLocaleString('en-IN')}` : '—'} mono />
                    <OrderField l="TX / Ref" v={selected.payment_tx_reference} mono />
                    <OrderField l="Submitted at" v={selected.payment_submitted_at ? new Date(selected.payment_submitted_at).toLocaleString() : '—'} />
                  </div>
                  {selected.payment_proof_url && (
                    <a href={selected.payment_proof_url} target="_blank" rel="noreferrer" className="block mt-3">
                      <img src={selected.payment_proof_url} alt="proof" className="max-h-56 rounded border border-[var(--eh-green)]" onError={e => { e.target.style.display = 'none'; }} />
                    </a>
                  )}
                </div>
              )}

              {/* Legacy panel placement (read-only for historical orders) */}
              <SmmPlacementPanel order={selected} />

              {/* Status updater */}
              <div>
                <div className="eh-mono text-[10px] opacity-60 mb-1">UPDATE STATUS</div>
                <div className="flex flex-wrap gap-1.5">
                  {ORDER_STATUSES.map(s => (
                    <button key={s.v} onClick={() => updateStatus(selected.id, s.v)} data-testid={`order-status-${s.v}`} className={`text-[10px] eh-mono tracking-widest px-3 py-1.5 rounded border ${selected.status === s.v ? 'border-[var(--eh-green)] text-[var(--eh-green)] bg-[rgba(0,255,157,.08)]' : 'border-[var(--eh-border)] hover:border-[var(--eh-green)]'}`}>{s.label}</button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t border-[var(--eh-border)] justify-between">
                <a href={`/track?id=${selected.id}${Number(selected.payment_amount || selected.amount || 0) > 0 ? '&pay=1' : ''}`} target="_blank" rel="noreferrer" className="eh-btn-ghost text-xs flex items-center gap-1.5"><Eye size={12} /> OPEN PUBLIC TRACKER</a>
                {selected.tg && <a href={`https://t.me/${(selected.tg || '').replace(/^@/, '')}`} target="_blank" rel="noreferrer" className="eh-btn-primary text-xs flex items-center gap-1.5"><Send size={12} /> CONTACT ON TELEGRAM</a>}
              </div>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

const FeedManager = () => {
  const { config, update } = useSiteConfig();
  const profile = config.feedProfile || { username: 'errorhacker', displayName: 'ERRORHACKER', bio: '', website: '', followers: 0, following: 0, verified: true };
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {kind:'post'|'reel', data}
  const [selectedComments, setSelectedComments] = useState(null); // {kind, id, items}

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.feedListPosts(), api.feedListReels()]);
      setPosts(p); setReels(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = (kind) => setEditing({ kind, data: kind === 'post'
    ? { image_url: '', caption: '', location: '', likes_base: 0, views_base: 0, pinned: false }
    : { video_url: '', thumb_url: '', caption: '', likes_base: 0, views_base: 0, pinned: false } });

  const save = async () => {
    if (!editing) return;
    const { kind, data } = editing;
    try {
      if (kind === 'post') {
        if (!data.image_url) { toast.error('Image URL required'); return; }
        if (data.id) await api.feedUpdatePost(data.id, { image_url: data.image_url, caption: data.caption || '', location: data.location || '', likes_base: Number(data.likes_base) || 0, views_base: Number(data.views_base) || 0, pinned: !!data.pinned });
        else await api.feedCreatePost({ ...data, likes_base: Number(data.likes_base) || 0, views_base: Number(data.views_base) || 0 });
      } else {
        if (!data.video_url) { toast.error('Video URL required'); return; }
        if (data.id) await api.feedUpdateReel(data.id, { video_url: data.video_url, thumb_url: data.thumb_url || '', caption: data.caption || '', likes_base: Number(data.likes_base) || 0, views_base: Number(data.views_base) || 0, pinned: !!data.pinned });
        else await api.feedCreateReel({ ...data, likes_base: Number(data.likes_base) || 0, views_base: Number(data.views_base) || 0 });
      }
      toast.success('Saved');
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (kind, id) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    try {
      if (kind === 'post') await api.feedDeletePost(id);
      else await api.feedDeleteReel(id);
      toast.success('Deleted'); refresh();
    } catch (e) { toast.error(e.message); }
  };

  const togglePin = async (kind, item) => {
    try {
      if (kind === 'post') await api.feedUpdatePost(item.id, { pinned: !item.pinned });
      else await api.feedUpdateReel(item.id, { pinned: !item.pinned });
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const openComments = async (kind, id) => {
    try {
      const items = kind === 'post' ? await api.feedPostComments(id) : await api.feedReelComments(id);
      setSelectedComments({ kind, id, items, newName: '', newText: '', newPic: '' });
    } catch (e) { toast.error(e.message); }
  };

  const addAdminComment = async () => {
    if (!selectedComments) return;
    const { kind, id, newName, newText, newPic } = selectedComments;
    if (!newName || !newText) { toast.error('Name and text required'); return; }
    try {
      const c = await api.feedAddAdminComment({ [kind === 'post' ? 'post_id' : 'reel_id']: id, user_name: newName, text: newText, picture: newPic || '' });
      setSelectedComments(s => ({ ...s, items: [...s.items, c], newName: '', newText: '', newPic: '' }));
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const delComment = async (cid) => {
    if (!window.confirm('Delete comment?')) return;
    try {
      await api.feedDeleteComment(cid);
      setSelectedComments(s => ({ ...s, items: s.items.filter(c => c.id !== cid) }));
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Section kicker="// SOCIAL" title="FEED MANAGER (POSTS · REELS · COMMENTS)" actions={<button onClick={refresh} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> REFRESH</button>}>
      {/* Profile config */}
      <div className="eh-panel p-5 mb-6">
        <div className="eh-kicker mb-3">// PROFILE HEADER</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><Label>USERNAME</Label><Input value={profile.username} onChange={e => update('feedProfile.username', e.target.value)} /></div>
          <div><Label>DISPLAY NAME</Label><Input value={profile.displayName} onChange={e => update('feedProfile.displayName', e.target.value)} /></div>
          <div><Label>WEBSITE</Label><Input value={profile.website || ''} onChange={e => update('feedProfile.website', e.target.value)} /></div>
          <div><Label hint="number">FOLLOWERS</Label><Input type="number" value={profile.followers} onChange={e => update('feedProfile.followers', Number(e.target.value) || 0)} /></div>
          <div><Label hint="number">FOLLOWING</Label><Input type="number" value={profile.following} onChange={e => update('feedProfile.following', Number(e.target.value) || 0)} /></div>
          <div><Label>VERIFIED BADGE</Label><label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer mt-2"><input type="checkbox" checked={!!profile.verified} onChange={e => update('feedProfile.verified', e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" /> {profile.verified ? 'ON' : 'OFF'}</label></div>
          <div className="sm:col-span-2 lg:col-span-3"><Label>BIO (multi-line)</Label><Textarea rows={3} value={profile.bio || ''} onChange={e => update('feedProfile.bio', e.target.value)} /></div>
        </div>
        <div className="border-t border-[var(--eh-border)] mt-5 pt-5">
          <div className="eh-kicker mb-3">// FOLLOW & MESSAGE BUTTONS</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label hint="full url e.g. https://instagram.com/yourhandle">FOLLOW URL (Instagram / Twitter / etc.)</Label><Input value={profile.instagram_url || ''} onChange={e => update('feedProfile.instagram_url', e.target.value)} placeholder="https://instagram.com/errorhacker" data-testid="admin-follow-url" /></div>
            <div><Label>FOLLOW BUTTON LABEL</Label><Input value={profile.follow_label || 'FOLLOW'} onChange={e => update('feedProfile.follow_label', e.target.value)} placeholder="FOLLOW" /></div>
            <div>
              <Label>MESSAGE BUTTON OPENS</Label>
              <select className="eh-input" value={profile.message_kind || 'telegram'} onChange={e => update('feedProfile.message_kind', e.target.value)} data-testid="admin-message-kind">
                <option value="telegram">Telegram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram DM</option>
                <option value="email">Email</option>
                <option value="url">Custom URL</option>
                <option value="track">In-site Order Tracker</option>
              </select>
            </div>
            <div>
              <Label hint="Telegram @handle, WhatsApp digits with country code, Instagram handle, email, or full URL">MESSAGE DESTINATION</Label>
              <Input value={profile.message_url || ''} onChange={e => update('feedProfile.message_url', e.target.value)} placeholder="@errorhacker / 919999999999 / you@x.com / https://..." data-testid="admin-message-url" />
            </div>
            <div className="sm:col-span-2"><Label>MESSAGE BUTTON LABEL</Label><Input value={profile.message_label || 'MESSAGE'} onChange={e => update('feedProfile.message_label', e.target.value)} placeholder="MESSAGE" /></div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('posts')} className={`px-4 py-2 rounded eh-mono text-xs tracking-widest ${tab==='posts' ? 'bg-[rgba(0,255,157,.15)] text-[var(--eh-green)] border border-[rgba(0,255,157,.4)]' : 'border border-[var(--eh-border)]'}`}>POSTS ({posts.length})</button>
        <button onClick={() => setTab('reels')} className={`px-4 py-2 rounded eh-mono text-xs tracking-widest ${tab==='reels' ? 'bg-[rgba(0,255,157,.15)] text-[var(--eh-green)] border border-[rgba(0,255,157,.4)]' : 'border border-[var(--eh-border)]'}`}>REELS ({reels.length})</button>
        <button onClick={() => openNew(tab === 'reels' ? 'reel' : 'post')} className="eh-btn-primary text-xs ml-auto"><Plus size={12} /> NEW {tab === 'reels' ? 'REEL' : 'POST'}</button>
      </div>

      {loading ? <div className="py-10 text-center opacity-60">Loading...</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(tab === 'posts' ? posts : reels).length === 0 && <div className="col-span-3 py-10 text-center opacity-60 eh-mono text-xs">No {tab} yet. Hit NEW above to create.</div>}
          {(tab === 'posts' ? posts : reels).map(item => (
            <div key={item.id} className="eh-panel overflow-hidden">
              <div className="aspect-square bg-black relative">
                {tab === 'posts'
                  ? <img src={item.image_url} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
                  : (item.thumb_url
                      ? <img src={item.thumb_url} alt="" className="w-full h-full object-cover" />
                      : <video src={item.video_url} className="w-full h-full object-cover" muted preload="metadata" />)
                }
                {item.pinned && <span className="absolute top-2 left-2 text-[9px] eh-mono px-1.5 py-0.5 rounded bg-black/70 text-[var(--eh-green)] tracking-widest">PINNED</span>}
              </div>
              <div className="p-3">
                <div className="text-sm leading-5 line-clamp-2 mb-2">{item.caption || <span className="opacity-50">(no caption)</span>}</div>
                <div className="flex justify-between eh-mono text-[11px] opacity-70 mb-3">
                  <span>❤ {item.likes_count}</span>
                  <span>💬 {item.comments_count}</span>
                  <span>👁 {item.views_count}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setEditing({ kind: tab === 'posts' ? 'post' : 'reel', data: { ...item } })} className="eh-btn-ghost text-[11px] px-2 py-1"><Edit3 size={11} /> EDIT</button>
                  <button onClick={() => openComments(tab === 'posts' ? 'post' : 'reel', item.id)} className="eh-btn-ghost text-[11px] px-2 py-1"><MessageSquare size={11} /> {item.comments_count}</button>
                  <button onClick={() => togglePin(tab === 'posts' ? 'post' : 'reel', item)} className="eh-btn-ghost text-[11px] px-2 py-1">{item.pinned ? 'UNPIN' : 'PIN'}</button>
                  <button onClick={() => remove(tab === 'posts' ? 'post' : 'reel', item.id)} className="eh-btn-ghost text-[11px] px-2 py-1 ml-auto text-red-400 hover:text-red-300"><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-xl eh-panel p-5 max-h-[90vh] overflow-y-auto" style={{ background: '#0d1115' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="eh-kicker">// {editing.data.id ? 'EDIT' : 'NEW'} {editing.kind.toUpperCase()}</div>
              <button onClick={() => setEditing(null)} className="opacity-70 hover:opacity-100"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              {editing.kind === 'post' ? (
                <>
                  <div><Label hint="upload or paste url">IMAGE</Label><ImageInput value={editing.data.image_url} onChange={(v) => setEditing(e => ({ ...e, data: { ...e.data, image_url: v } }))} /></div>
                  {editing.data.image_url && <img src={editing.data.image_url} className="max-h-48 rounded object-cover w-full" alt="" onError={e => e.target.style.display='none'} />}
                  <div><Label>CAPTION</Label><Textarea rows={3} value={editing.data.caption} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, caption: e.target.value } }))} /></div>
                  <div><Label>LOCATION (optional)</Label><Input value={editing.data.location} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, location: e.target.value } }))} placeholder="darknet" /></div>
                </>
              ) : (
                <>
                  <FeedVideoUpload value={editing.data.video_url} onChange={(v) => setEditing(e => ({ ...e, data: { ...e.data, video_url: v } }))} />
                  {editing.data.video_url && <video src={editing.data.video_url} controls className="max-h-64 w-full bg-black rounded" />}
                  <div><Label hint="optional thumbnail image">THUMB URL</Label><ImageInput value={editing.data.thumb_url} onChange={(v) => setEditing(e => ({ ...e, data: { ...e.data, thumb_url: v } }))} /></div>
                  <div><Label>CAPTION</Label><Textarea rows={3} value={editing.data.caption} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, caption: e.target.value } }))} /></div>
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label hint="display likes start">LIKES BASE</Label><Input type="number" value={editing.data.likes_base} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, likes_base: Number(e.target.value) || 0 } }))} /></div>
                <div><Label hint="display views start">VIEWS BASE</Label><Input type="number" value={editing.data.views_base} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, views_base: Number(e.target.value) || 0 } }))} /></div>
              </div>
              <label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer"><input type="checkbox" checked={!!editing.data.pinned} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, pinned: e.target.checked } }))} className="w-4 h-4 accent-[var(--eh-green)]" /> PINNED (shows first)</label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="eh-btn-ghost text-xs">CANCEL</button>
              <button onClick={save} className="eh-btn-primary text-xs"><Save size={12} /> SAVE</button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {selectedComments && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setSelectedComments(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-lg eh-panel p-5 max-h-[90vh] overflow-y-auto" style={{ background: '#0d1115' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="eh-kicker">// COMMENTS ({selectedComments.items.length})</div>
              <button onClick={() => setSelectedComments(null)} className="opacity-70 hover:opacity-100"><X size={16} /></button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2 mb-4">
              {selectedComments.items.length === 0 && <div className="opacity-60 eh-mono text-xs text-center py-4">No comments yet.</div>}
              {selectedComments.items.map(c => (
                <div key={c.id} className="flex items-start gap-3 eh-panel p-3">
                  <div className="flex-1">
                    <div className="text-sm"><span className="font-bold mr-2">{c.user_name}</span><span className="opacity-90">{c.text}</span></div>
                    <div className="eh-mono text-[10px] opacity-50 mt-1">{c.is_admin_seed ? '⚙ seeded · ' : ''}{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => delComment(c.id)} className="text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-[var(--eh-border)] pt-3 space-y-2">
              <div className="eh-kicker mb-2">// ADD SEEDED COMMENT</div>
              <Input value={selectedComments.newName} onChange={e => setSelectedComments(s => ({ ...s, newName: e.target.value }))} placeholder="username e.g. ghost_runner" />
              <Input value={selectedComments.newPic} onChange={e => setSelectedComments(s => ({ ...s, newPic: e.target.value }))} placeholder="avatar URL (optional)" />
              <Textarea rows={2} value={selectedComments.newText} onChange={e => setSelectedComments(s => ({ ...s, newText: e.target.value }))} placeholder="comment text" />
              <button onClick={addAdminComment} className="eh-btn-primary text-xs"><Plus size={12} /> ADD COMMENT</button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

// Helper: video upload component
const FeedVideoUpload = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('video/')) { toast.error('Pick a video'); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error('Max 50MB'); return; }
    setBusy(true);
    try {
      const r = await api.feedUploadMedia(f);
      onChange(r.absoluteUrl);
      toast.success('Video uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label hint="upload mp4 (max 50MB) or paste url">VIDEO</Label>
      <div className="flex gap-2 items-stretch">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="> https://... or upload" />
        <label className="eh-btn-ghost text-xs cursor-pointer whitespace-nowrap px-3">
          {busy ? <><Loader2 className="animate-spin" size={12} /> UPLOADING</> : <><Upload size={12} /> UPLOAD MP4</>}
          <input type="file" accept="video/*" onChange={onPick} className="hidden" disabled={busy} />
        </label>
        {value && <button type="button" onClick={() => onChange('')} className="eh-btn-ghost text-xs px-3"><X size={12} /></button>}
      </div>
    </div>
  );
};

const UsersTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const load = async () => {
    setLoading(true);
    try { setItems(await api.listUsers()); } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await api.deleteUser(u.user_id); toast.success('Deleted'); load(); } catch (e) { toast.error(e.message); }
  };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };
  const filtered = items.filter(u => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.email || '').toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s) || (u.referral_code || '').toLowerCase().includes(s);
  });
  return (
    <Section kicker="// USERS" title={`REGISTERED USERS (${items.length})`} actions={<button onClick={load} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> REFRESH</button>}>
      <div className="mb-4">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="> search email / name / referral..." className="eh-input text-sm max-w-md" data-testid="users-search" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div className="eh-panel p-4">
          <div className="eh-mono text-[10px] opacity-60 mb-1">TOTAL</div>
          <div className="eh-display text-2xl font-black eh-neon">{items.length}</div>
        </div>
        <div className="eh-panel p-4">
          <div className="eh-mono text-[10px] opacity-60 mb-1">PASSWORD</div>
          <div className="eh-display text-2xl font-black">{items.filter(u => u.provider === 'password').length}</div>
        </div>
        <div className="eh-panel p-4">
          <div className="eh-mono text-[10px] opacity-60 mb-1">GOOGLE</div>
          <div className="eh-display text-2xl font-black">{items.filter(u => u.provider === 'google').length}</div>
        </div>
      </div>
      <div className="eh-panel overflow-x-auto">
        <table className="w-full eh-mono text-sm min-w-[760px]">
          <thead><tr className="text-left border-b border-[var(--eh-border)]">
            <th className="p-3 text-xs tracking-widest opacity-70">USER</th>
            <th className="p-3 text-xs">EMAIL</th>
            <th className="p-3 text-xs">PROVIDER</th>
            <th className="p-3 text-xs">REFERRAL</th>
            <th className="p-3 text-xs">ORDERS</th>
            <th className="p-3 text-xs">JOINED</th>
            <th className="p-3 text-xs text-right">ACTIONS</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center opacity-60">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center opacity-60">No users.</td></tr>}
            {filtered.map(u => (
              <tr key={u.user_id} className="border-b border-[var(--eh-border)] hover:bg-white/[.02]" data-testid={`user-row-${u.user_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {u.picture ? <img src={u.picture} className="w-7 h-7 rounded-full object-cover" alt="" /> : <div className="w-7 h-7 rounded-full grid place-items-center text-[10px]" style={{ background: 'rgba(0,255,157,.15)', color: 'var(--eh-green)' }}>{(u.name || u.email || 'a')[0].toUpperCase()}</div>}
                    <span className="text-sm font-bold" style={{ fontFamily: 'Inter,sans-serif' }}>{u.name || '—'}</span>
                  </div>
                </td>
                <td className="p-3"><span className="eh-neon-soft">{u.email}</span></td>
                <td className="p-3"><span className="px-2 py-1 rounded text-[10px]" style={{ background: u.provider === 'google' ? 'rgba(77,224,255,.1)' : 'rgba(0,255,157,.1)', color: u.provider === 'google' ? '#4de0ff' : 'var(--eh-green)' }}>{(u.provider || 'password').toUpperCase()}</span></td>
                <td className="p-3 opacity-80">{u.referral_code || '—'}</td>
                <td className="p-3">{u.orders_count}</td>
                <td className="p-3 opacity-70 text-[11px]">{new Date(u.created_at).toLocaleString()}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => copy(u.email)} title="Copy email" className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] hover:text-[var(--eh-green)]"><Copy size={12} /></button>
                    <button onClick={() => remove(u)} className="w-7 h-7 grid place-items-center rounded border border-[var(--eh-border)] hover:border-red-400 hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const ReferralsTab = () => {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([api.getReferralSettings(), api.adminReferrals()]);
      setSettings(s); setHistory(h);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const upd = (patch) => setSettings(s => ({ ...s, ...patch }));
  const save = async () => {
    setSaving(true);
    try { const r = await api.putReferralSettings(settings); setSettings(r); toast.success('Saved'); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  if (loading) return <Section kicker="// REFERRALS" title="REFERRAL PROGRAM"><div className="py-10 text-center opacity-60">Loading...</div></Section>;
  const totalPaid = history.reduce((s, r) => s + Number(r.amount || 0), 0);
  return (
    <Section kicker="// REFERRALS" title="REFERRAL PROGRAM" actions={<button onClick={save} disabled={saving} className="eh-btn-primary text-xs"><Save size={12} /> {saving ? 'SAVING...' : 'SAVE SETTINGS'}</button>}>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div className="eh-panel p-4"><div className="eh-mono text-[10px] opacity-60 mb-1">TOTAL REWARDS PAID</div><div className="eh-display text-3xl font-black eh-neon">{settings?.currency_symbol || '₹'}{totalPaid.toFixed(0)}</div></div>
        <div className="eh-panel p-4"><div className="eh-mono text-[10px] opacity-60 mb-1">REFERRAL EVENTS</div><div className="eh-display text-3xl font-black">{history.length}</div></div>
        <div className="eh-panel p-4"><div className="eh-mono text-[10px] opacity-60 mb-1">PROGRAM</div><div className="eh-display text-2xl font-black" style={{ color: settings.enabled ? 'var(--eh-green)' : '#ffc828' }}>{settings.enabled ? 'ACTIVE' : 'PAUSED'}</div></div>
      </div>
      <div className="eh-panel p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="eh-kicker">// REWARD RULES</div>
          <label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer">
            <input type="checkbox" checked={!!settings.enabled} onChange={e => upd({ enabled: e.target.checked })} className="w-4 h-4 accent-[var(--eh-green)]" />
            {settings.enabled ? 'ENABLED' : 'DISABLED'}
          </label>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div><Label hint="awarded to inviter on signup">SIGNUP REWARD</Label><Input type="number" value={settings.signup_reward} onChange={e => upd({ signup_reward: Number(e.target.value) || 0 })} /></div>
          <div><Label hint="% of invitee's first order">ORDER COMMISSION %</Label><Input type="number" value={settings.order_percent} onChange={e => upd({ order_percent: Number(e.target.value) || 0 })} /></div>
          <div><Label>MIN PAYOUT</Label><Input type="number" value={settings.min_payout} onChange={e => upd({ min_payout: Number(e.target.value) || 0 })} /></div>
          <div><Label>CURRENCY SYMBOL</Label><Input value={settings.currency_symbol} onChange={e => upd({ currency_symbol: e.target.value })} /></div>
        </div>
        <div className="mt-3"><Label>PROGRAM DESCRIPTION (shown to user)</Label><Textarea rows={2} value={settings.description || ''} onChange={e => upd({ description: e.target.value })} /></div>
      </div>
      <div className="eh-panel overflow-x-auto">
        <div className="eh-kicker p-4 pb-2">// RECENT REWARDS</div>
        <table className="w-full eh-mono text-sm min-w-[640px]">
          <thead><tr className="text-left border-b border-[var(--eh-border)]">
            <th className="p-3 text-xs">DATE</th>
            <th className="p-3 text-xs">INVITER</th>
            <th className="p-3 text-xs">INVITEE</th>
            <th className="p-3 text-xs">TYPE</th>
            <th className="p-3 text-xs text-right">AMOUNT</th>
          </tr></thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={5} className="p-6 text-center opacity-60">No rewards yet.</td></tr>}
            {history.map(r => (
              <tr key={r.id} className="border-b border-[var(--eh-border)]">
                <td className="p-3 opacity-70 text-[11px]">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-3 eh-neon-soft">{r.inviter_id}</td>
                <td className="p-3">{r.invitee_email}</td>
                <td className="p-3 opacity-80">{r.type}</td>
                <td className="p-3 text-right font-bold text-[var(--eh-green)]">{settings.currency_symbol}{Number(r.amount || 0).toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

const PaymentSettingsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getPaymentSettings().then(setData).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);
  const update = (patch) => setData(d => ({ ...d, ...patch }));
  const updateWallet = (i, patch) => setData(d => ({ ...d, crypto_wallets: d.crypto_wallets.map((w, idx) => idx === i ? { ...w, ...patch } : w) }));
  const addWallet = () => setData(d => ({ ...d, crypto_wallets: [...(d.crypto_wallets || []), { coin: '', network: '', address: '', qr_url: '' }] }));
  const removeWallet = (i) => setData(d => ({ ...d, crypto_wallets: d.crypto_wallets.filter((_, idx) => idx !== i) }));
  const updateCurrency = (i, patch) => setData(d => ({ ...d, currencies: d.currencies.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const addCurrency = () => setData(d => ({ ...d, currencies: [...(d.currencies || []), { code: '', symbol: '', rate: 1 }] }));
  const removeCurrency = (i) => setData(d => ({ ...d, currencies: d.currencies.filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true);
    try { const r = await api.putPaymentSettings(data); setData(r); toast.success('Saved'); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  if (loading) return <Section kicker="// PAYMENTS" title="PAYMENT SETTINGS"><div className="py-10 text-center opacity-60">Loading...</div></Section>;
  return (
    <Section kicker="// PAYMENTS" title="PAYMENT SETTINGS" actions={<button onClick={save} disabled={saving} className="eh-btn-primary text-xs"><Save size={12} /> {saving ? 'SAVING...' : 'SAVE ALL'}</button>}>
      {/* Manual / UPI */}
      <div className="eh-panel p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="eh-kicker">// MANUAL (UPI / BANK)</div>
          <label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer"><input type="checkbox" checked={!!data.manual_enabled} onChange={e => update({ manual_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--eh-green)]" /> {data.manual_enabled ? 'ENABLED' : 'DISABLED'}</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>UPI ID</Label><Input value={data.upi_id || ''} onChange={e => update({ upi_id: e.target.value })} placeholder="errorhacker@upi" /></div>
          <div><Label>UPI NAME / RECIPIENT</Label><Input value={data.upi_name || ''} onChange={e => update({ upi_name: e.target.value })} placeholder="ERRORHACKER" /></div>
          <div className="sm:col-span-2"><Label>QR CODE IMAGE</Label><ImageInput value={data.qr_image_url || ''} onChange={v => update({ qr_image_url: v })} testid="payment-qr" /></div>
          <div className="sm:col-span-2"><Label>BANK DETAILS (multi-line)</Label><Textarea rows={3} value={data.bank_details || ''} onChange={e => update({ bank_details: e.target.value })} placeholder="Bank: HDFC\nA/C: 1234 5678 9012\nIFSC: HDFC0001234" /></div>
          <div className="sm:col-span-2"><Label>INSTRUCTIONS (shown to customer at checkout)</Label><Textarea rows={3} value={data.instructions || ''} onChange={e => update({ instructions: e.target.value })} /></div>
        </div>
      </div>

      {/* Crypto */}
      <div className="eh-panel p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="eh-kicker">// CRYPTO WALLETS</div>
          <label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer"><input type="checkbox" checked={!!data.crypto_enabled} onChange={e => update({ crypto_enabled: e.target.checked })} className="w-4 h-4 accent-[var(--eh-green)]" /> {data.crypto_enabled ? 'ENABLED' : 'DISABLED'}</label>
        </div>
        <div className="space-y-3">
          {(data.crypto_wallets || []).map((w, i) => (
            <div key={i} className="grid sm:grid-cols-[120px_140px_1fr_auto] gap-2 items-end border border-[var(--eh-border)] p-3 rounded">
              <div><Label>COIN</Label><Input value={w.coin} onChange={e => updateWallet(i, { coin: e.target.value.toUpperCase() })} placeholder="BTC" /></div>
              <div><Label>NETWORK</Label><Input value={w.network} onChange={e => updateWallet(i, { network: e.target.value })} placeholder="Bitcoin / TRC20" /></div>
              <div><Label>ADDRESS</Label><Input value={w.address} onChange={e => updateWallet(i, { address: e.target.value })} placeholder="bc1q... / T..." /></div>
              <button onClick={() => removeWallet(i)} className="eh-btn-ghost text-xs text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
              <div className="sm:col-span-4"><Label>QR (optional)</Label><ImageInput value={w.qr_url} onChange={v => updateWallet(i, { qr_url: v })} testid={`wallet-qr-${i}`} /></div>
            </div>
          ))}
          <button onClick={addWallet} className="eh-btn-ghost text-xs"><Plus size={12} /> ADD WALLET</button>
        </div>
      </div>

      {/* Currencies */}
      <div className="eh-panel p-5">
        <div className="eh-kicker mb-3">// CURRENCIES & RATES</div>
        <p className="eh-mono text-[11px] opacity-70 mb-3">Set rate as multiplier from your BASE price. Example: if your service is priced in INR and you want to show USD, USD rate = 0.012 means 1 INR × 0.012 = USD.</p>
        <div className="space-y-2 max-w-xl">
          {(data.currencies || []).map((c, i) => (
            <div key={i} className="grid grid-cols-[100px_100px_1fr_auto] gap-2 items-end">
              <div><Label>CODE</Label><Input value={c.code} onChange={e => updateCurrency(i, { code: e.target.value.toUpperCase() })} placeholder="USD" /></div>
              <div><Label>SYMBOL</Label><Input value={c.symbol} onChange={e => updateCurrency(i, { symbol: e.target.value })} placeholder="$" /></div>
              <div><Label>RATE × BASE</Label><Input type="number" step="0.0001" value={c.rate} onChange={e => updateCurrency(i, { rate: Number(e.target.value) || 0 })} /></div>
              <button onClick={() => removeCurrency(i)} className="eh-btn-ghost text-xs text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={addCurrency} className="eh-btn-ghost text-xs"><Plus size={12} /> ADD CURRENCY</button>
        </div>
        <div className="mt-4 max-w-xs">
          <Label>DEFAULT CURRENCY</Label>
          <select className="eh-input" value={data.default_currency || 'INR'} onChange={e => update({ default_currency: e.target.value })}>
            {(data.currencies || []).map(c => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
          </select>
        </div>
      </div>
    </Section>
  );
};

const CouponsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'percent', value: 10, max_uses: -1, active: true, description: '' });
  const load = async () => {
    setLoading(true);
    try { setItems(await api.listCoupons()); } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!form.code) { toast.error('Code required'); return; }
    try { await api.createCoupon({ ...form, code: form.code.toUpperCase(), value: Number(form.value), max_uses: Number(form.max_uses) }); toast.success('Coupon created'); setForm({ code: '', type: 'percent', value: 10, max_uses: -1, active: true, description: '' }); load(); }
    catch (err) { toast.error(err.message); }
  };
  const toggleActive = async (c) => {
    try { await api.updateCoupon(c.code, { active: !c.active }); load(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await api.deleteCoupon(code); toast.success('Deleted'); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Section kicker="// DISCOUNTS" title="COUPON CODES" actions={<button onClick={load} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> REFRESH</button>}>
      <div className="grid lg:grid-cols-[420px_1fr] gap-5">
        <form onSubmit={create} className="eh-panel p-5 space-y-3" data-testid="coupon-form">
          <div className="eh-kicker mb-2">// CREATE</div>
          <Label>CODE</Label>
          <Input data-testid="coupon-code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="HACK20" />
          <Label>TYPE</Label>
          <select className="eh-input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="coupon-type">
            <option value="percent">PERCENT (%)</option>
            <option value="flat">FLAT (amount off)</option>
          </select>
          <Label hint={form.type === 'percent' ? '0-100' : 'currency amount'}>VALUE</Label>
          <Input data-testid="coupon-value" type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
          <Label hint="-1 = unlimited">MAX USES</Label>
          <Input data-testid="coupon-max-uses" type="number" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} />
          <Label>DESCRIPTION</Label>
          <Input data-testid="coupon-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="20% off all services" />
          <label className="inline-flex items-center gap-2 eh-mono text-xs cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[var(--eh-green)]" /> ACTIVE
          </label>
          <button data-testid="coupon-create-btn" type="submit" className="eh-btn-primary text-xs w-full justify-center"><Plus size={12} /> CREATE COUPON</button>
        </form>
        <div className="eh-panel overflow-x-auto">
          <table className="w-full eh-mono text-sm min-w-[640px]">
            <thead><tr className="text-left border-b border-[var(--eh-border)]">
              <th className="p-3 text-xs tracking-widest opacity-70">CODE</th>
              <th className="p-3 text-xs">TYPE</th>
              <th className="p-3 text-xs">VALUE</th>
              <th className="p-3 text-xs">USED</th>
              <th className="p-3 text-xs">STATUS</th>
              <th className="p-3 text-xs">ACTIONS</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-center opacity-60">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center opacity-60">No coupons yet.</td></tr>}
              {items.map(c => (
                <tr key={c.code} className="border-b border-[var(--eh-border)]" data-testid={`coupon-row-${c.code}`}>
                  <td className="p-3 eh-neon-soft font-bold">{c.code}</td>
                  <td className="p-3 opacity-80">{c.type}</td>
                  <td className="p-3">{c.type === 'percent' ? `${c.value}%` : c.value}</td>
                  <td className="p-3 opacity-70">{c.used} / {c.max_uses === -1 ? '∞' : c.max_uses}</td>
                  <td className="p-3"><span className="px-2 py-1 rounded text-[11px]" style={{ background: c.active ? 'rgba(0,255,157,.1)' : 'rgba(255,255,255,.05)', color: c.active ? 'var(--eh-green)' : 'rgba(255,255,255,.5)' }}>{c.active ? 'ACTIVE' : 'OFF'}</span></td>
                  <td className="p-3 flex gap-2">
                    <button onClick={() => toggleActive(c)} className="eh-btn-ghost text-[11px] px-2 py-1">{c.active ? 'DISABLE' : 'ENABLE'}</button>
                    <button onClick={() => remove(c.code)} className="text-red-400 hover:text-red-300 px-2"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
};

const NotificationsTab = () => {
  const { config, update } = useSiteConfig();
  const t = (config.notifications && config.notifications.telegram) || { enabled: false, bot_token: '', chat_id: '' };
  const ga = config.analytics || { enabled: false, googleId: '' };
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    if (!t.bot_token || !t.chat_id) { toast.error('Bot Token and Chat ID are required'); return; }
    setTesting(true);
    try {
      await api.testTelegram(t.bot_token, t.chat_id, 'ERRORHACKER // test alert ok_');
      toast.success('Test sent — check your Telegram');
    } catch (e) { toast.error(e.message); }
    finally { setTesting(false); }
  };
  return (
    <Section kicker="// CHANNELS" title="ORDER NOTIFICATIONS & ANALYTICS">
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="eh-panel p-5 space-y-4">
          <div className="flex items-center justify-between">
            <Label hint="ping bot whenever a new order arrives">TELEGRAM ALERTS</Label>
            <label className="inline-flex items-center gap-2 text-xs eh-mono cursor-pointer">
              <input type="checkbox" data-testid="tg-enabled" checked={!!t.enabled} onChange={e => update('notifications.telegram.enabled', e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" />
              {t.enabled ? <span className="text-[var(--eh-green)]">ENABLED</span> : <span className="opacity-60">DISABLED</span>}
            </label>
          </div>
          <Label hint="from @BotFather (e.g. 7891234567:AAH...)">BOT TOKEN</Label>
          <Input data-testid="tg-bot-token" value={t.bot_token || ''} onChange={e => update('notifications.telegram.bot_token', e.target.value)} placeholder="> bot token" />
          <Label hint="from @userinfobot (numeric)">CHAT ID</Label>
          <Input data-testid="tg-chat-id" value={t.chat_id || ''} onChange={e => update('notifications.telegram.chat_id', e.target.value)} placeholder="> chat id" />
          <div className="flex gap-2 pt-1">
            <button data-testid="tg-test-btn" onClick={sendTest} disabled={testing} className="eh-btn-primary text-xs">{testing ? 'SENDING...' : 'SEND TEST'}</button>
          </div>
        </div>
        <div className="eh-panel p-5 space-y-3 eh-mono text-[12px] opacity-80 leading-6">
          <div className="eh-kicker mb-1">// TELEGRAM SETUP</div>
          <div>1. Open Telegram → search <span className="text-[var(--eh-green)]">@BotFather</span> → send <span className="text-[var(--eh-green)]">/newbot</span>.</div>
          <div>2. Pick a name + username ending in <span className="text-[var(--eh-green)]">_bot</span>. Copy the <b>BOT TOKEN</b>.</div>
          <div>3. Search <span className="text-[var(--eh-green)]">@userinfobot</span> → press Start → copy your <b>CHAT ID</b>.</div>
          <div>4. Paste both here, enable, hit <b>SEND TEST</b>.</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="eh-panel p-5 space-y-4">
          <div className="flex items-center justify-between">
            <Label hint="track visitors in real time">GOOGLE ANALYTICS</Label>
            <label className="inline-flex items-center gap-2 text-xs eh-mono cursor-pointer">
              <input type="checkbox" data-testid="ga-enabled" checked={!!ga.enabled} onChange={e => update('analytics.enabled', e.target.checked)} className="w-4 h-4 accent-[var(--eh-green)]" />
              {ga.enabled ? <span className="text-[var(--eh-green)]">ENABLED</span> : <span className="opacity-60">DISABLED</span>}
            </label>
          </div>
          <Label hint="format: G-XXXXXXXXXX">MEASUREMENT ID</Label>
          <Input data-testid="ga-id" value={ga.googleId || ''} onChange={e => update('analytics.googleId', e.target.value.trim())} placeholder="> G-XXXXXXXXXX" />
          {ga.googleId && !/^G-[A-Z0-9]+$/i.test(ga.googleId) && (
            <div className="eh-mono text-[11px]" style={{ color: '#ffc828' }}>⚠ ID should start with <b>G-</b></div>
          )}
          <div className="eh-mono text-[11px] opacity-70 leading-6 pt-1">
            Tracks every page view + auto-loads <span className="text-[var(--eh-green)]">gtag.js</span> on the public site (not in admin). Verify in Google Analytics → Realtime within 1 min.
          </div>
        </div>
        <div className="eh-panel p-5 space-y-3 eh-mono text-[12px] opacity-80 leading-6">
          <div className="eh-kicker mb-1">// GOOGLE ANALYTICS SETUP</div>
          <div>1. Go to <span className="text-[var(--eh-green)]">analytics.google.com</span> → Admin → Create Property.</div>
          <div>2. Add a <b>Web</b> data stream for <span className="text-[var(--eh-green)]">errorhacker.site</span>.</div>
          <div>3. Copy your <b>Measurement ID</b> (format: <span className="text-[var(--eh-green)]">G-ABC1234567</span>).</div>
          <div>4. Paste it here, toggle <b>ENABLED</b>, hit <b>SAVE</b> at top.</div>
          <div>5. Open your site in another tab → check GA <b>Realtime</b> report to confirm.</div>
        </div>
      </div>

      <div className="mt-8 pt-8 border-t border-[var(--eh-border)]">
        <div className="eh-kicker mb-3">// CUSTOMER BOT</div>
        <TelegramBotPanel />
      </div>
    </Section>
  );
};

const SettingsTab = () => {
  const { config, update, setConfig, reset, refetch } = useSiteConfig();
  const [newPw, setNewPw] = useState('');
  const changePass = async () => {
    if (!newPw || newPw.length < 4) { toast.error('Password too short (min 4 chars)'); return; }
    try {
      await api.changePassword(newPw);
      toast.success('Password updated. Re-login required.');
      localStorage.removeItem('eh_admin_token');
      localStorage.removeItem('eh_admin');
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.message); }
  };
  const exportConfig = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `errorhacker-config-${Date.now()}.json`; a.click();
    toast.success('Config exported');
  };
  const importConfig = (e) => {
    const f = e.target.files?.[0]; if(!f) return;
    const r = new FileReader();
    r.onload = () => { try { setConfig({ ...config, ...JSON.parse(r.result) }); toast.success('Config imported & synced'); } catch { toast.error('Invalid JSON'); } };
    r.readAsText(f);
  };
  return (
    <Section kicker="// SYSTEM" title="SETTINGS" actions={<button onClick={refetch} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> SYNC FROM SERVER</button>}>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="eh-panel p-5">
          <Label hint="used to access /admin">CHANGE ADMIN PASSWORD</Label>
          <div className="flex gap-2">
            <Input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="> new password (min 4 chars)" />
            <button onClick={changePass} className="eh-btn-primary text-xs"><Save size={12} /> SAVE</button>
          </div>
          <div className="eh-mono text-[11px] opacity-60 mt-2">You will be logged out after change.</div>
        </div>
        <div className="eh-panel p-5">
          <Label>BACKUP & RESTORE</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            <button onClick={exportConfig} className="eh-btn-ghost text-xs"><Download size={12} /> EXPORT JSON</button>
            <label className="eh-btn-ghost text-xs cursor-pointer"><Upload size={12} /> IMPORT JSON<input type="file" accept="application/json" onChange={importConfig} className="hidden" /></label>
            <button onClick={() => { if(window.confirm('Reset ALL settings to defaults on the server? This cannot be undone.')) { reset(); toast.success('Reset complete'); }}} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> RESET ALL</button>
          </div>
          <div className="eh-mono text-[11px] opacity-60 mt-3">Changes auto-sync to the live server when you are logged in.</div>
        </div>
      </div>
    </Section>
  );
};

// ============================================================================
//  COMMAND OVERVIEW — full-fat monitoring dashboard
//  Pulls from /api/admin/overview + /api/admin/overview/health +
//  /api/admin/overview/activity. Theme kept intact (pure black + neon accents).
// ============================================================================

const nfmt = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return Math.round(v).toLocaleString();
};
const cfmt = (n) => `$${nfmt(n)}`;
const rfmt = (n) => `₹${nfmt(n)}`;

const KpiTile = ({ label, value, hint, accent = '#00ff9d', icon: Icon, warn = false, testid, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className="eh-panel p-4 relative overflow-hidden group transition-all hover:-translate-y-0.5 text-left"
      style={warn ? { borderColor: 'rgba(255,200,40,.45)', boxShadow: '0 10px 40px -20px rgba(255,200,40,.5)', cursor: onClick ? 'pointer' : 'default' } : { borderColor: `${accent}33`, cursor: onClick ? 'pointer' : 'default' }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="eh-mono text-[10px] tracking-widest opacity-60">{label}</div>
        {Icon && (
          <div className="w-7 h-7 rounded grid place-items-center shrink-0" style={{ background: `${warn ? '#ffc828' : accent}18`, color: warn ? '#ffc828' : accent }}>
            <Icon size={13} />
          </div>
        )}
      </div>
      <div className="eh-display text-2xl sm:text-3xl font-black leading-none" style={{ color: warn ? '#ffc828' : accent }}>
        {value}
      </div>
      {hint && <div className="eh-mono text-[10px] opacity-55 mt-1.5">{hint}</div>}
      {onClick && <div className="eh-mono text-[9px] opacity-60 mt-1.5 group-hover:opacity-100 transition-opacity" style={{ color: accent }}>CLICK → OPEN DRILLDOWN</div>}
      <div className="absolute inset-x-0 bottom-0 h-[2px] opacity-70" style={{ background: `linear-gradient(90deg, transparent, ${warn ? '#ffc828' : accent}, transparent)` }} />
    </Tag>
  );
};

// Compact trend chart (SVG, no external deps). Preserves theme.
const RevenueTrend = ({ points }) => {
  const max = Math.max(1, ...points.map(p => Number(p.revenue || 0)));
  const w = 800, h = 140, pad = 12;
  const bw = (w - pad * 2) / points.length;
  return (
    <div className="eh-panel p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="eh-kicker">// REVENUE · LAST 7 DAYS</div>
        <div className="eh-mono text-[10px] opacity-60">peak {cfmt(max)}</div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
        <defs>
          <linearGradient id="revgrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#00ff9d" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#00ff9d" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {points.map((p, i) => {
          const barH = ((Number(p.revenue) || 0) / max) * (h - pad * 2 - 18);
          const x = pad + i * bw + 4;
          const y = h - pad - barH - 18;
          return (
            <g key={p.iso}>
              <rect x={x} y={y} width={bw - 8} height={barH} fill="url(#revgrad)" rx="2" />
              <text x={x + (bw - 8) / 2} y={y - 4} textAnchor="middle" fontSize="9" fill="#00ff9d" opacity="0.85" fontFamily="ui-monospace,monospace">{p.revenue > 0 ? `$${nfmt(p.revenue)}` : ''}</text>
              <text x={x + (bw - 8) / 2} y={h - 4} textAnchor="middle" fontSize="9" fill="#d6e2dc" opacity="0.5" fontFamily="ui-monospace,monospace">{p.date}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const StatusChip = ({ label, count, color }) => (
  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded border text-[10px] eh-mono tracking-widest" style={{ borderColor: `${color}55`, background: `${color}14`, color }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
    {label} <span className="opacity-90 font-black">{count}</span>
  </div>
);

const STATUS_COLORS = {
  received:        '#4de0ff',
  payment_review:  '#ffc828',
  verified:        '#c084fc',
  'in-progress':   '#4de0ff',
  in_progress:     '#4de0ff',
  paid:            '#00ff9d',
  delivered:       '#00ff9d',
  cancelled:       '#ff5078',
  canceled:        '#ff5078',
  refunded:        '#ff8a4d',
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const HealthPill = ({ ok, label }) => (
  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border text-[10px] eh-mono tracking-widest" style={{ borderColor: ok ? 'rgba(0,255,157,.5)' : 'rgba(255,80,120,.5)', background: ok ? 'rgba(0,255,157,.1)' : 'rgba(255,80,120,.1)', color: ok ? '#00ff9d' : '#ff5078' }}>
    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: ok ? '#00ff9d' : '#ff5078' }} />
    {label}
  </div>
);

const KIND_ICON = {
  signup:   User,
  order:    ShoppingBag,
  spin:     Sparkles,
  recovery: ShieldCheck,
  tool:     Terminal,
};
const KIND_COLOR = {
  signup:   '#4de0ff',
  order:    '#00ff9d',
  spin:     '#ffd34d',
  recovery: '#ff2d92',
  tool:     '#c084fc',
};

// ============================================================================
//  WALLET FLOAT · Drill-down modal
//  Full user list with balance / flags / actions. Fetches /admin/wallets and
//  offers per-row deduct (with email receipt) + ban toggle + txn history.
// ============================================================================
const RISK_COLORS = {
  banned:                   '#ff5078',
  high_balance_no_orders:   '#ffc828',
  spin_farmer:              '#ff2d92',
  hoarding:                 '#c084fc',
  new_account_high_balance: '#ff8a4d',
};
const RISK_LABELS = {
  banned:                   'BANNED',
  high_balance_no_orders:   'HIGH BAL · NO ORDERS',
  spin_farmer:              'SPIN FARMER',
  hoarding:                 'HOARDING',
  new_account_high_balance: 'FRESH · HIGH BAL',
};

const WalletFloatModal = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState('balance');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);          // opened txn drilldown user
  const [txns, setTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [adjust, setAdjust] = useState({ amount: '', note: '', type: 'debit', send_receipt: true });
  const [banConfirm, setBanConfirm] = useState(null);
  const [banBusy, setBanBusy] = useState(false);
  const [adjustBusy, setAdjustBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.adminWalletsList(sort);
      setRows(Array.isArray(r) ? r : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load wallets');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) refresh(); }, [open, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTxns = async (row) => {
    setSelected(row);
    setTxnsLoading(true);
    setTxns([]);
    try {
      const r = await api.adminWalletUserTxns(row.user_id, 200);
      setTxns(Array.isArray(r) ? r : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load transactions');
    } finally {
      setTxnsLoading(false);
    }
  };

  const submitAdjust = async () => {
    if (!selected || !adjust.amount || Number(adjust.amount) <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setAdjustBusy(true);
    try {
      const res = await api.adminWalletAdjustReceipt(selected.user_id, {
        amount: Number(adjust.amount),
        note: adjust.note || `Admin ${adjust.type}`,
        type: adjust.type,
        send_receipt: !!adjust.send_receipt,
      });
      toast.success(`Wallet ${adjust.type} · ₹${Number(adjust.amount).toFixed(2)}${res.receipt_sent ? ' · receipt sent' : ''}`);
      setAdjust({ amount: '', note: '', type: 'debit', send_receipt: true });
      await openTxns(selected);   // refresh txns
      await refresh();             // refresh top list
    } catch (e) {
      toast.error(e.message || 'Adjustment failed');
    } finally {
      setAdjustBusy(false);
    }
  };

  const submitBan = async () => {
    if (!banConfirm) return;
    const targetBan = !banConfirm.disabled;
    setBanBusy(true);
    try {
      await api.adminUserBan(banConfirm.user_id, { ban: targetBan, reason: banConfirm.reason || '' });
      toast.success(targetBan ? 'User banned' : 'User unbanned');
      setBanConfirm(null);
      await refresh();
      if (selected && selected.user_id === banConfirm.user_id) {
        setSelected({ ...selected, disabled: targetBan });
      }
    } catch (e) {
      toast.error(e.message || 'Ban toggle failed');
    } finally {
      setBanBusy(false);
    }
  };

  if (!open) return null;

  const filtered = rows.filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.email || '').toLowerCase().includes(s)
        || (r.name || '').toLowerCase().includes(s)
        || (r.user_id || '').toLowerCase().includes(s);
  });

  const total = rows.reduce((s, r) => s + (r.balance || 0), 0);
  const riskyCount = rows.filter(r => (r.flags || []).length > 0).length;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto" style={{ background: 'rgba(0,0,0,.85)', backdropFilter: 'blur(8px)' }} data-testid="wallet-float-modal">
      <div className="w-full max-w-6xl m-3 sm:m-6 mb-24 rounded-2xl border-2" style={{ background: 'linear-gradient(180deg, #050705, #030403)', borderColor: 'rgba(255,211,77,.4)', boxShadow: '0 30px 80px -20px rgba(255,211,77,.35)' }}>
        {/* Header */}
        <div className="p-5 flex items-center justify-between gap-3 border-b" style={{ borderColor: 'rgba(255,211,77,.25)' }}>
          <div>
            <div className="eh-kicker mb-1" style={{ color: '#ffd34d' }}>// WALLET FLOAT · DRILLDOWN</div>
            <div className="eh-display text-xl sm:text-2xl font-black">
              ₹{total.toLocaleString()} <span className="opacity-60 text-sm">across {rows.length} wallets · <span style={{ color: '#ff5078' }}>{riskyCount} flagged</span></span>
            </div>
          </div>
          <button onClick={onClose} className="eh-btn-ghost text-xs" data-testid="wallet-float-close">
            <X size={12} /> CLOSE
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-[var(--eh-border)]">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search email / name / user_id…"
            className="eh-input text-xs flex-1 min-w-[180px]"
            data-testid="wallet-float-search"
          />
          <select value={sort} onChange={e => setSort(e.target.value)} className="eh-input text-xs" data-testid="wallet-float-sort">
            <option value="balance">Sort: Balance (desc)</option>
            <option value="recent">Sort: Recent activity</option>
            <option value="risky">Sort: Risk score</option>
          </select>
          <button onClick={refresh} className="eh-btn-ghost text-xs"><RefreshCcw size={11} className={loading ? 'animate-spin' : ''} /> REFRESH</button>
        </div>

        {/* Body — split when a user is selected */}
        <div className={`grid ${selected ? 'lg:grid-cols-2' : ''} gap-0`}>
          {/* List — hidden on mobile once a user is picked, to avoid nested scrollers */}
          <div className={`${selected ? 'hidden lg:block' : ''} max-h-[65vh] overflow-y-auto eh-scroll`}>
            {loading && <div className="p-6 text-center opacity-60 eh-mono text-xs"><Loader2 size={12} className="inline animate-spin" /> Loading…</div>}
            {!loading && filtered.length === 0 && <div className="p-6 text-center opacity-60 eh-mono text-xs">No wallets found.</div>}
            {filtered.map(r => (
              <div
                key={r.user_id}
                onClick={() => openTxns(r)}
                data-testid={`wallet-row-${r.user_id}`}
                className={`p-3.5 border-b border-[var(--eh-border)] flex items-center gap-3 cursor-pointer hover:bg-white/[.03] ${selected?.user_id === r.user_id ? 'bg-white/[.05]' : ''}`}
              >
                {r.picture ? (
                  <img src={r.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full grid place-items-center text-[10px] eh-mono shrink-0" style={{ background: 'rgba(255,211,77,.15)', color: '#ffd34d' }}>
                    {((r.name || r.email || 'a')[0] || 'a').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold truncate flex items-center gap-1.5" style={{ fontFamily: 'Inter,sans-serif' }}>
                    {r.name || (r.email || '').split('@')[0] || '—'}
                    {r.disabled && <span className="eh-mono text-[8px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#ff5078', color: '#000' }}>BANNED</span>}
                    {r.tg_linked && <Bot size={9} className="text-[#4de0ff]" />}
                  </div>
                  <div className="eh-mono text-[10px] opacity-60 truncate">{r.email}</div>
                  {(r.flags || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.flags.map(f => (
                        <span key={f} className="eh-mono text-[8.5px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${RISK_COLORS[f] || '#ff5078'}22`, color: RISK_COLORS[f] || '#ff5078', border: `1px solid ${RISK_COLORS[f] || '#ff5078'}55` }}>
                          {RISK_LABELS[f] || f.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="eh-display text-lg font-black" style={{ color: r.balance > 500 ? '#ffd34d' : '#00ff9d' }}>₹{Number(r.balance || 0).toLocaleString()}</div>
                  <div className="eh-mono text-[9.5px] opacity-60">{r.orders_count} ord · {r.txn_count} txn</div>
                </div>
              </div>
            ))}
          </div>

          {/* Drilldown pane */}
          {selected && (
            <div className="border-l border-[var(--eh-border)] p-4 max-h-[65vh] overflow-y-auto eh-scroll" data-testid="wallet-drill">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="text-[15px] font-bold truncate" style={{ fontFamily: 'Inter,sans-serif' }}>
                    {selected.name} <span className="opacity-60 font-normal text-xs">({selected.user_id})</span>
                  </div>
                  <div className="eh-mono text-[10px] opacity-70 truncate">{selected.email}</div>
                  <div className="eh-display text-3xl font-black mt-1" style={{ color: '#ffd34d' }}>₹{Number(selected.balance || 0).toLocaleString()}</div>
                  <div className="eh-mono text-[10px] opacity-60 mt-1">
                    Credited: ₹{selected.total_credit} · Debited: ₹{selected.total_debit} · Net: ₹{selected.net}
                  </div>
                </div>
                <button onClick={() => { setSelected(null); setTxns([]); }} className="eh-btn-ghost text-[10px]"><X size={10} /></button>
              </div>

              {/* Actions — Adjust + Ban */}
              <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: 'rgba(0,255,157,.35)', background: 'rgba(0,255,157,.04)' }}>
                <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-2">// ADJUST WALLET</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={adjust.type} onChange={e => setAdjust({ ...adjust, type: e.target.value })} className="eh-input text-xs" data-testid="wallet-adjust-type">
                    <option value="debit">Debit (-)</option>
                    <option value="credit">Credit (+)</option>
                  </select>
                  <input
                    type="number"
                    value={adjust.amount}
                    onChange={e => setAdjust({ ...adjust, amount: e.target.value })}
                    placeholder="Amount ₹"
                    className="eh-input text-xs"
                    data-testid="wallet-adjust-amount"
                  />
                </div>
                <input
                  value={adjust.note}
                  onChange={e => setAdjust({ ...adjust, note: e.target.value })}
                  placeholder={adjust.type === 'debit' ? 'Reason (shown in email receipt)' : 'Reason (e.g. compensation)'}
                  className="eh-input text-xs w-full mb-2"
                  data-testid="wallet-adjust-note"
                />
                <label className="flex items-center gap-2 text-[11px] eh-mono cursor-pointer mb-2 opacity-90">
                  <input type="checkbox" checked={adjust.send_receipt} onChange={e => setAdjust({ ...adjust, send_receipt: e.target.checked })} className="accent-[var(--eh-green)]" />
                  <Mail size={10} /> Email receipt to {selected.email || 'user'}
                </label>
                <button onClick={submitAdjust} disabled={adjustBusy} className="eh-btn-primary text-xs w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="wallet-adjust-submit">
                  {adjustBusy ? <><Loader2 size={11} className="animate-spin inline mr-1" /> APPLYING…</> : <><Save size={11} /> APPLY {adjust.type.toUpperCase()}</>}
                </button>
              </div>

              {/* Ban toggle */}
              <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: selected.disabled ? 'rgba(0,255,157,.35)' : 'rgba(255,80,120,.4)', background: selected.disabled ? 'rgba(0,255,157,.04)' : 'rgba(255,80,120,.05)' }}>
                <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-2">// ACCOUNT STATUS</div>
                {selected.disabled ? (
                  <>
                    <div className="text-[12px] mb-2 opacity-80">User is currently <strong className="text-[#ff5078]">BANNED</strong>. They cannot log in or spend wallet.</div>
                    <button onClick={() => setBanConfirm({ ...selected, reason: '' })} className="eh-btn-ghost text-xs w-full" style={{ color: '#00ff9d', borderColor: 'rgba(0,255,157,.5)' }} data-testid="wallet-unban">
                      <Check size={11} /> UNBAN THIS USER
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-[12px] mb-2 opacity-80">Banning suspends login + freezes wallet. Reversible.</div>
                    <button onClick={() => setBanConfirm({ ...selected, reason: '' })} className="eh-btn-ghost text-xs w-full" style={{ color: '#ff5078', borderColor: 'rgba(255,80,120,.5)' }} data-testid="wallet-ban">
                      <Lock size={11} /> BAN THIS USER
                    </button>
                  </>
                )}
              </div>

              {/* Transactions */}
              <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-2">// LEDGER · LAST {txns.length}</div>
              {txnsLoading && <div className="text-center py-4 opacity-60 eh-mono text-xs"><Loader2 size={11} className="inline animate-spin" /> loading…</div>}
              {!txnsLoading && txns.length === 0 && <div className="text-center py-4 opacity-60 eh-mono text-xs">No transactions.</div>}
              <div className="space-y-1.5">
                {txns.map(t => {
                  const isCredit = ['credit', 'cashback', 'refund', 'spin'].includes(t.type);
                  const color = isCredit ? '#00ff9d' : '#ff5078';
                  return (
                    <div key={t.id} className="flex items-center gap-2 p-2 rounded border" style={{ borderColor: `${color}22`, background: `${color}05` }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11.5px] font-bold truncate" style={{ fontFamily: 'Inter,sans-serif' }}>{t.note || t.type}</div>
                        <div className="eh-mono text-[9.5px] opacity-60 truncate">{t.id} · {(t.createdAt || '').slice(0, 16).replace('T', ' ')}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="eh-mono text-[12px] font-black" style={{ color }}>{isCredit ? '+' : '−'}₹{Number(t.amount).toLocaleString()}</div>
                        <div className="eh-mono text-[9px] opacity-60">bal: ₹{Number(t.balance_after || 0).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ban confirmation modal */}
      {banConfirm && (
        <div className="fixed inset-0 z-[90] grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.75)' }}>
          <div className="w-full max-w-md rounded-xl border-2 p-5" style={{ borderColor: banConfirm.disabled ? 'rgba(0,255,157,.5)' : 'rgba(255,80,120,.5)', background: '#050705' }}>
            <div className="eh-kicker mb-2" style={{ color: banConfirm.disabled ? '#00ff9d' : '#ff5078' }}>// CONFIRM {banConfirm.disabled ? 'UNBAN' : 'BAN'}</div>
            <div className="text-sm mb-3" style={{ fontFamily: 'Inter,sans-serif' }}>
              {banConfirm.disabled ? 'Restore login and wallet access for' : 'Suspend login and freeze wallet for'}{' '}
              <strong>{banConfirm.name || banConfirm.email}</strong>?
            </div>
            <input
              value={banConfirm.reason}
              onChange={e => setBanConfirm({ ...banConfirm, reason: e.target.value })}
              placeholder="Reason (audit log)"
              className="eh-input text-xs w-full mb-3"
              data-testid="wallet-ban-reason"
            />
            <div className="flex gap-2">
              <button onClick={() => setBanConfirm(null)} disabled={banBusy} className="eh-btn-ghost text-xs flex-1 disabled:opacity-50">CANCEL</button>
              <button onClick={submitBan} disabled={banBusy} className="eh-btn-primary text-xs flex-1 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="wallet-ban-confirm" style={banConfirm.disabled ? {} : { background: '#ff5078', boxShadow: '0 8px 24px -8px #ff5078' }}>
                {banBusy ? <><Loader2 size={11} className="animate-spin inline mr-1" /> WORKING…</> : 'CONFIRM'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Overview = ({ setActive }) => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [walletModal, setWalletModal] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setErr('');
    try {
      const [d, h, a] = await Promise.all([
        api.adminOverview().catch(e => { throw e; }),
        api.adminOverviewHealth().catch(() => null),
        api.adminOverviewActivity(30).catch(() => ({ events: [] })),
      ]);
      setData(d); setHealth(h); setActivity(a?.events || []);
    } catch (e) {
      setErr(e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  const updateOrderStatus = async (id, status) => {
    try { await api.updateOrder(id, status); toast.success(`Marked ${status}`); refresh(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading && !data) {
    return (
      <Section kicker="// DASHBOARD" title="COMMAND OVERVIEW">
        <div className="eh-panel p-10 text-center eh-mono text-xs opacity-70"><Loader2 className="animate-spin inline mr-2" size={14} /> Booting monitors…</div>
      </Section>
    );
  }
  if (err && !data) {
    return (
      <Section kicker="// DASHBOARD" title="COMMAND OVERVIEW" actions={<button onClick={refresh} className="eh-btn-ghost text-xs"><RefreshCcw size={12} /> RETRY</button>}>
        <div className="eh-panel p-8 text-center border border-red-500/40 text-red-300 eh-mono text-xs">{err}</div>
      </Section>
    );
  }

  const kpi = data?.kpi || {};
  const orders = data?.orders || {};
  const smm = data?.smm || {};
  const tools = data?.tools || {};
  const recovery = data?.recovery?.funnel || {};
  const recoveryCases24h = data?.recovery?.cases_24h || 0;
  const payments = data?.payments || {};
  const spin = data?.spin || {};
  const recentUsers = data?.users?.recent || [];

  const uptimeH = health?.uptime_s ? `${Math.floor(health.uptime_s / 3600)}h ${Math.floor((health.uptime_s % 3600) / 60)}m` : '—';
  const dbLat = health?.db_latency_ms;

  const quickActions = [
    { label: 'ORDERS',       icon: ShoppingBag,  to: 'orders',    color: '#00ff9d' },
    { label: 'PAYMENTS',     icon: CreditCard,   to: 'payments',  color: '#4de0ff' },
    { label: 'REFUNDS',      icon: RotateCcw,    to: 'refunds',   color: '#ff8a4d' },
    { label: 'CONSULTATION', icon: ShieldCheck,  to: 'recovery',  color: '#c084fc' },
    { label: 'BROADCAST',    icon: Megaphone,    to: 'announcements', color: '#ffd34d' },
    { label: 'USERS',        icon: User,         to: 'users',     color: '#4de0ff' },
    { label: 'SETTINGS',     icon: Settings,     to: 'settings',  color: '#00ff9d' },
  ];

  return (
    <Section
      kicker="// DASHBOARD · LIVE"
      title="COMMAND OVERVIEW"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <HealthPill ok={!!health?.ok && !!health?.db_ok} label={`SYS ${health?.ok ? 'ONLINE' : 'DEGRADED'}`} />
          <HealthPill ok={!!health?.db_ok} label={`DB ${dbLat != null ? dbLat + 'ms' : 'DOWN'}`} />
          <HealthPill ok={!!health?.emergent_llm_key} label="LLM" />
          <HealthPill ok={!!health?.cashfree_configured} label="CASHFREE" />
          <HealthPill ok={!!health?.telegram_configured} label="TG BOT" />
          <HealthPill ok={!!health?.resend_configured} label="RESEND" />
          <button onClick={refresh} className="eh-btn-ghost text-xs" data-testid="ov-refresh">
            <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} /> {loading ? '' : 'REFRESH'}
          </button>
        </div>
      }
    >
      {/* Uptime strip */}
      <div className="eh-mono text-[10px] opacity-55 mb-4 flex flex-wrap gap-4 tracking-widest">
        <span>UPTIME · <span className="text-[var(--eh-green)]">{uptimeH}</span></span>
        <span>SERVER · <span className="opacity-90">{new Date(data?.server_time || Date.now()).toLocaleTimeString()}</span></span>
        <span>AUTO-REFRESH · <span className="text-[#4de0ff]">30s</span></span>
      </div>

      {/* KPI GRID — the "everything visible" strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-6">
        <KpiTile testid="kpi-users"       label="USERS"          value={nfmt(kpi.users_total)} hint={`+${kpi.users_24h || 0} · 24h · ${kpi.users_disabled || 0} disabled`} icon={User}       accent="#4de0ff" />
        <KpiTile testid="kpi-orders"      label="ORDERS"         value={nfmt(kpi.orders_total)} hint={`+${kpi.orders_24h || 0} · 24h`}         icon={ShoppingBag} accent="#00ff9d" />
        <KpiTile testid="kpi-revenue"     label="REVENUE (ALL)"  value={cfmt(kpi.revenue_all)}  hint={`7d ${cfmt(kpi.revenue_7d)} · 24h ${cfmt(kpi.revenue_24h)}`} icon={CreditCard} accent="#00ff9d" />
        <KpiTile testid="kpi-pending"     label="PENDING REVIEW" value={kpi.orders_pending_review || 0} hint="verify payments →" icon={AlertTriangle} warn={(kpi.orders_pending_review || 0) > 0} />
        <KpiTile testid="kpi-wallet"      label="WALLET FLOAT"   value={rfmt(kpi.wallet_in_circulation)} hint={`${kpi.wallet_holders || 0} holders · ${kpi.wallet_deposits_pending || 0} deposits pending`} icon={Zap} accent="#ffd34d" warn={(kpi.wallet_deposits_pending || 0) > 0} onClick={() => setWalletModal(true)} />
        <KpiTile testid="kpi-refunds"     label="REFUNDS"        value={kpi.refunds_pending || 0} hint={`${kpi.refunds_total || 0} lifetime`} icon={RotateCcw} accent="#ff8a4d" warn={(kpi.refunds_pending || 0) > 0} />
        <KpiTile testid="kpi-recovery"    label="CONSULT QUEUE"  value={(recovery.new || 0) + (recovery.reviewing || 0)} hint={`${recovery.total || 0} lifetime`} icon={ShieldCheck} accent="#c084fc" />
        <KpiTile testid="kpi-tools"       label="AI TOOLS · 24h" value={nfmt(tools.usage_24h)} hint={`${nfmt(tools.total_all)} lifetime`} icon={Terminal} accent="#c084fc" />
        <KpiTile testid="kpi-feed"        label="FEED CONTENT"   value={nfmt((kpi.feed_posts || 0) + (kpi.feed_reels || 0))} hint={`${kpi.feed_views_24h || 0} views · 24h`} icon={Activity} accent="#4de0ff" />
        <KpiTile testid="kpi-spin"        label="SPIN WHEEL"     value={nfmt(spin.spins_24h)} hint={`${nfmt(spin.spins_all)} lifetime · biggest ${spin.biggest_amount ? '₹'+nfmt(spin.biggest_amount) : '—'}`} icon={Sparkles} accent="#ffd34d" />
        <KpiTile testid="kpi-tg"          label="TG LINKED"      value={nfmt(kpi.bot_users_linked)} hint={`${kpi.coupons_active || 0} coupons · ${kpi.announcements_active || 0} broadcasts`} icon={Send} accent="#4de0ff" />
      </div>

      {/* Quick actions */}
      <div className="eh-panel p-4 mb-6">
        <div className="eh-kicker mb-3">// JUMP</div>
        <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {quickActions.map(q => { const I = q.icon; return (
            <button
              key={q.label}
              onClick={() => setActive(q.to)}
              className="flex flex-col items-center gap-2 py-3.5 rounded border border-[var(--eh-border)] hover:-translate-y-0.5 transition-all"
              style={{ background: `${q.color}0a` }}
              data-testid={`qa-${q.to}`}
            >
              <div className="w-8 h-8 rounded grid place-items-center" style={{ background: `${q.color}18`, color: q.color, border: `1px solid ${q.color}45` }}><I size={14} /></div>
              <div className="eh-mono text-[9.5px] tracking-widest opacity-90">{q.label}</div>
            </button>
          );})}
        </div>
      </div>

      {/* Revenue trend + Order status */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <RevenueTrend points={orders.trend_7d || []} />
        </div>
        <div className="eh-panel p-4">
          <div className="eh-kicker mb-3">// ORDER STATUS</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(orders.status_counts || {}).sort((a,b) => b[1] - a[1]).map(([k, v]) => (
              <StatusChip key={k} label={k.replace(/_/g,' ').toUpperCase()} count={v} color={STATUS_COLORS[k] || '#c084fc'} />
            ))}
            {(!orders.status_counts || Object.keys(orders.status_counts).length === 0) && (
              <div className="text-xs opacity-60 eh-mono">No orders yet.</div>
            )}
          </div>
          <button onClick={() => setActive('orders')} className="mt-4 eh-mono text-[10px] opacity-70 hover:opacity-100 hover:text-[var(--eh-green)]">MANAGE ORDERS →</button>
        </div>
      </div>

      {/* AI Tools + Consultation Funnel — 2 up */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="eh-panel p-4" data-testid="ov-tools">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker flex items-center gap-2"><Terminal size={12} /> AI TOOLS · 24h</div>
            <button onClick={() => setActive('tools')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">TUNE →</button>
          </div>
          <div className="space-y-1.5">
            {(tools.top || []).length === 0 && <div className="text-xs opacity-60 eh-mono py-3">No usage in the last 24h.</div>}
            {(tools.top || []).slice(0, 6).map((t, i) => {
              const max = Math.max(1, ...(tools.top || []).map(x => x.count));
              const pct = Math.round((t.count / max) * 100);
              return (
                <div key={t.tool}>
                  <div className="flex items-center justify-between text-[11px] eh-mono">
                    <span className="truncate">{i + 1}. {t.tool}</span>
                    <span className="text-[var(--eh-green)] font-black">{t.count}</span>
                  </div>
                  <div className="h-1 rounded bg-white/5 mt-1 overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#00ff9d,#c084fc)' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="eh-mono text-[10px] opacity-60 mt-3">{nfmt(tools.total_all)} lifetime tool invocations</div>
        </div>

        <div className="eh-panel p-4" data-testid="ov-recovery">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker flex items-center gap-2"><ShieldCheck size={12} /> CONSULTATION FUNNEL</div>
            <button onClick={() => setActive('recovery')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">OPEN →</button>
          </div>
          <div className="space-y-2">
            {[
              { k: 'new',        c: '#4de0ff', l: 'NEW' },
              { k: 'reviewing',  c: '#ffc828', l: 'IN REVIEW' },
              { k: 'quoted',     c: '#c084fc', l: 'QUOTED' },
              { k: 'paid',       c: '#00ff9d', l: 'PAID' },
              { k: 'in_progress',c: '#4de0ff', l: 'IN OP' },
              { k: 'resolved',   c: '#00ff9d', l: 'RESOLVED' },
              { k: 'closed',     c: '#666',    l: 'CLOSED' },
            ].map(row => {
              const v = recovery[row.k] || 0;
              const max = Math.max(1, ...Object.entries(recovery).filter(([k]) => k !== 'total').map(([, val]) => val));
              return (
                <div key={row.k} className="flex items-center gap-2">
                  <div className="eh-mono text-[10px] w-20 opacity-70">{row.l}</div>
                  <div className="flex-1 h-1.5 rounded bg-white/5 overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${Math.round((v/max)*100)}%`, background: row.c }} />
                  </div>
                  <div className="eh-mono text-[11px] font-black w-8 text-right" style={{ color: row.c }}>{v}</div>
                </div>
              );
            })}
          </div>
          <div className="eh-mono text-[10px] opacity-60 mt-3">{recovery.total || 0} lifetime cases · <span className="text-[#4de0ff]">{recoveryCases24h} · 24h</span></div>
        </div>
      </div>

      {/* Payments monitor */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="eh-panel p-4" data-testid="ov-payments">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker flex items-center gap-2"><CreditCard size={12} /> PAYMENT METHODS</div>
            <button onClick={() => setActive('payments')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">MANAGE →</button>
          </div>
          <div className="space-y-2">
            {(payments.method_split || []).length === 0 && <div className="text-xs opacity-60 eh-mono py-3">No verified payments yet.</div>}
            {(payments.method_split || []).map((m, i) => {
              const totalAmt = (payments.method_split || []).reduce((s, x) => s + x.amount, 0) || 1;
              const pct = Math.round((m.amount / totalAmt) * 100);
              const c = ['#00ff9d', '#4de0ff', '#c084fc', '#ffd34d', '#ff2d92'][i % 5];
              return (
                <div key={m.method}>
                  <div className="flex items-center justify-between text-[11px] eh-mono">
                    <span className="uppercase" style={{ color: c }}>{m.method}</span>
                    <span>{m.count} orders · {cfmt(m.amount)} ({pct}%)</span>
                  </div>
                  <div className="h-1 rounded bg-white/5 mt-1"><div className="h-full rounded" style={{ width: `${pct}%`, background: c }} /></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="eh-panel p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker flex items-center gap-2"><Sparkles size={12} /> SPIN WHEEL</div>
            <button onClick={() => setActive('spin')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">EDIT PRIZES →</button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="p-2 rounded border border-[var(--eh-border)] text-center"><div className="eh-mono text-[9px] opacity-60">TODAY</div><div className="text-xl font-black eh-neon">{spin.spins_24h || 0}</div></div>
            <div className="p-2 rounded border border-[var(--eh-border)] text-center"><div className="eh-mono text-[9px] opacity-60">LIFETIME</div><div className="text-xl font-black">{nfmt(spin.spins_all)}</div></div>
            <div className="p-2 rounded border border-[var(--eh-border)] text-center"><div className="eh-mono text-[9px] opacity-60">BIGGEST</div><div className="text-xl font-black" style={{ color: '#ffd34d' }}>{spin.biggest_amount ? `₹${nfmt(spin.biggest_amount)}` : '—'}</div></div>
          </div>
          <div className="space-y-1.5">
            {(spin.wins_by_prize || []).slice(0, 4).map((p) => (
              <div key={p.label} className="flex items-center justify-between text-[11px] eh-mono">
                <span className="truncate opacity-80">{p.label}</span>
                <span className="text-[var(--eh-green)]">{p.count}× · ₹{nfmt(p.amount)}</span>
              </div>
            ))}
            {(spin.wins_by_prize || []).length === 0 && <div className="text-xs opacity-60 eh-mono py-2">No spins yet.</div>}
          </div>
        </div>
      </div>

      {/* Recent orders (inline update) + Recent users + Live activity */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="eh-panel p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker">// RECENT ORDERS</div>
            <button onClick={() => setActive('orders')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">ALL →</button>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto eh-scroll">
            {(orders.recent || []).length === 0 && <div className="py-6 text-center opacity-60 eh-mono text-xs">No orders yet.</div>}
            {(orders.recent || []).map(o => (
              <div key={o.id} className="flex items-center gap-2.5 p-2.5 border border-[var(--eh-border)] rounded" data-testid={`ov-order-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold truncate" style={{ fontFamily: 'Inter,sans-serif' }}>{o.service}</div>
                  <div className="eh-mono text-[10px] opacity-60 truncate">{o.customer} · {cfmt(o.amount)} · {timeAgo(o.at)}</div>
                </div>
                <select value={o.status} onChange={e => updateOrderStatus(o.id, e.target.value)} className="bg-transparent eh-mono text-[10px] px-1.5 py-1 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] cursor-pointer">
                  <option value="received">received</option>
                  <option value="payment_review">payment_review</option>
                  <option value="verified">verified</option>
                  <option value="in-progress">in-progress</option>
                  <option value="delivered">delivered</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="eh-panel p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker">// RECENT USERS</div>
            <button onClick={() => setActive('users')} className="eh-mono text-[10px] opacity-70 hover:text-[var(--eh-green)]">ALL →</button>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto eh-scroll">
            {recentUsers.length === 0 && <div className="py-6 text-center opacity-60 eh-mono text-xs">No users yet.</div>}
            {recentUsers.map(u => (
              <div key={u.user_id || u.email} className="flex items-center gap-2.5 p-2.5 border border-[var(--eh-border)] rounded">
                {u.picture ? (
                  <img src={u.picture} className="w-8 h-8 rounded-full object-cover shrink-0" alt="" />
                ) : (
                  <div className="w-8 h-8 rounded-full grid place-items-center text-[10px] eh-mono shrink-0" style={{ background: 'rgba(0,255,157,.15)', color: 'var(--eh-green)' }}>
                    {((u.name || u.email || 'a')[0] || 'a').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold truncate flex items-center gap-1" style={{ fontFamily: 'Inter,sans-serif' }}>
                    {u.name}
                    {u.tier && <span className="text-[8.5px] eh-mono px-1 py-0.5 rounded" style={{ background: 'rgba(255,211,77,.14)', color: '#ffd34d' }}>{u.tier}</span>}
                  </div>
                  <div className="eh-mono text-[10px] opacity-60 truncate">{u.email}</div>
                </div>
                <div className="eh-mono text-[10px] opacity-70 shrink-0">{u.orders_count} ord</div>
              </div>
            ))}
          </div>
        </div>

        <div className="eh-panel p-4" data-testid="ov-activity">
          <div className="flex items-center justify-between mb-3">
            <div className="eh-kicker flex items-center gap-2"><Activity size={12} /> LIVE ACTIVITY</div>
            <span className="eh-mono text-[10px] text-[#4de0ff]">{activity.length}</span>
          </div>
          <div className="space-y-1.5 max-h-[380px] overflow-y-auto eh-scroll">
            {activity.length === 0 && <div className="py-6 text-center opacity-60 eh-mono text-xs">Nothing yet.</div>}
            {activity.map((e, i) => {
              const Icon = KIND_ICON[e.kind] || Activity;
              const color = KIND_COLOR[e.kind] || '#00ff9d';
              return (
                <div key={i} className="flex items-start gap-2.5 p-2 rounded border border-[var(--eh-border)]" style={{ background: `${color}06` }}>
                  <div className="w-6 h-6 rounded grid place-items-center shrink-0 mt-0.5" style={{ background: `${color}18`, color, border: `1px solid ${color}45` }}><Icon size={11} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] font-bold truncate" style={{ fontFamily: 'Inter,sans-serif' }}>{e.title}</div>
                    <div className="eh-mono text-[9.5px] opacity-60 truncate">{e.detail}</div>
                  </div>
                  <div className="eh-mono text-[9.5px] opacity-55 shrink-0 mt-0.5">{timeAgo(e.at)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Wallet Float drilldown modal */}
      <WalletFloatModal open={walletModal} onClose={() => setWalletModal(false)} />
    </Section>
  );
};

const Login = ({ onOk }) => {
  const [tab, setTab] = useState('admin');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modEmail, setModEmail] = useState('');
  const [modPw, setModPw] = useState('');
  const submitAdmin = async e => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await api.login(pw);
      if (res?.token) {
        localStorage.setItem('eh_admin_token', res.token);
        localStorage.setItem('eh_admin', '1');
        onOk('owner');
      }
    } catch (err) { toast.error(err.status === 401 ? 'Access denied' : (err.message || 'Login failed')); }
    finally { setBusy(false); }
  };
  const submitMod = async e => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await api.authLogin(modEmail.trim().toLowerCase(), modPw);
      const role = res?.user?.role;
      if (!role || !['owner', 'feed_mod'].includes(role)) {
        toast.error('This account is not a team member.');
      } else {
        onOk(role);
      }
    } catch (err) { toast.error(err.status === 401 ? 'Invalid email or password' : (err.message || 'Login failed')); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center eh-grid-bg p-6">
      <div className="w-full max-w-sm eh-panel eh-brackets p-7"><span className="br-bl" /><span className="br-br" />
        <div className="flex items-center gap-3 mb-5"><Logo size={40} /><div><div className="eh-brand font-black tracking-widest eh-neon-soft">CONTROL</div><div className="text-[10px] eh-mono opacity-60">// {tab === 'admin' ? 'admin login' : 'team member login'}</div></div></div>
        <div className="flex gap-1 mb-4">
          <button type="button" onClick={()=>setTab('admin')} className={`flex-1 text-[11px] eh-mono tracking-widest px-3 py-2 rounded ${tab==='admin'?'bg-[rgba(0,255,157,.15)] text-[var(--eh-green)] border border-[var(--eh-green)]':'border border-[var(--eh-border)]'}`}>OWNER</button>
          <button type="button" onClick={()=>setTab('mod')} className={`flex-1 text-[11px] eh-mono tracking-widest px-3 py-2 rounded ${tab==='mod'?'bg-[rgba(0,255,157,.15)] text-[var(--eh-green)] border border-[var(--eh-green)]':'border border-[var(--eh-border)]'}`} data-testid="admin-login-mod-tab">MODERATOR</button>
        </div>
        {tab === 'admin' ? (
          <form onSubmit={submitAdmin}>
            <div className="eh-mono text-xs opacity-70 mb-2 flex items-center gap-2"><Lock size={12} color="var(--eh-green)" /> ACCESS_KEY</div>
            <div className="relative">
              <input type={show?'text':'password'} value={pw} onChange={e=>setPw(e.target.value)} placeholder="> ********" className="eh-input pr-10" autoFocus />
              <button type="button" onClick={()=>setShow(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
            <button disabled={busy} className="eh-btn-primary w-full mt-5 text-xs">{busy ? 'AUTHENTICATING...' : 'AUTHENTICATE'}</button>
            <div className="eh-mono text-[11px] opacity-50 mt-4 text-center">default: admin123 · change after first login</div>
          </form>
        ) : (
          <form onSubmit={submitMod}>
            <div className="eh-mono text-xs opacity-70 mb-2 flex items-center gap-2"><Mail size={12} color="var(--eh-green)" /> EMAIL</div>
            <input type="email" value={modEmail} onChange={e=>setModEmail(e.target.value)} placeholder="mod@example.com" className="eh-input mb-3" autoFocus data-testid="admin-mod-email" />
            <div className="eh-mono text-xs opacity-70 mb-2 flex items-center gap-2"><Lock size={12} color="var(--eh-green)" /> PASSWORD</div>
            <input type="password" value={modPw} onChange={e=>setModPw(e.target.value)} placeholder="********" className="eh-input" data-testid="admin-mod-pw" />
            <button disabled={busy} className="eh-btn-primary w-full mt-5 text-xs" data-testid="admin-mod-submit">{busy ? 'AUTHENTICATING...' : 'AUTHENTICATE'}</button>
            <div className="eh-mono text-[11px] opacity-50 mt-4 text-center">// only team members can enter</div>
          </form>
        )}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'owner' | 'feed_mod' | null
  const [active, setActive] = useState('overview');

  useEffect(() => {
    // Owner via admin token?
    if (localStorage.getItem('eh_admin') === '1' && localStorage.getItem('eh_admin_token')) {
      setMode('owner'); return;
    }
    // Mod via user JWT?
    api.authMe().then(resp => {
      const u = resp?.user || resp;
      if (u && ['owner', 'feed_mod'].includes(u.role) && !u.disabled) {
        setMode(u.role);
        if (u.role === 'feed_mod') setActive('feed');
      } else {
        setMode(false);
      }
    }).catch(() => setMode(false));
  }, []);

  if (mode === null) return <div className="min-h-screen grid place-items-center eh-grid-bg"><Loader2 className="animate-spin" /></div>;
  if (!mode) return <Login onOk={(r) => { setMode(r); if (r === 'feed_mod') setActive('feed'); }} />;

  const logout = async () => {
    try { await api.logout(); } catch (_) { /* noop */ }
    localStorage.removeItem('eh_admin'); localStorage.removeItem('eh_admin_token');
    setMode(false); navigate('/admin');
  };

  const allowed = (tab) => {
    if (mode === 'owner') return true;
    return tab === 'feed';
  };

  return (
    <div className="min-h-screen flex bg-[var(--eh-bg)]">
      <Sidebar active={active} setActive={setActive} onLogout={logout} mode={mode} />
      <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
        <div className="md:hidden mb-4 flex gap-2 overflow-x-auto eh-scroll pb-2">
          {sections.filter(s => mode === 'owner' || s.to === 'feed').map(s => (<button key={s.to} onClick={()=>setActive(s.to)} className={`shrink-0 px-3 py-2 rounded text-[11px] eh-mono tracking-widest uppercase ${active===s.to ? 'bg-[rgba(0,255,157,.15)] text-[var(--eh-green)] border border-[rgba(0,255,157,.4)]' : 'border border-[var(--eh-border)]'}`}>{s.label}</button>))}
        </div>

        {mode === 'feed_mod' && (
          <div className="mb-4 eh-panel p-3 flex items-center gap-2 bg-[rgba(0,255,157,.04)]">
            <ShieldCheck size={14} className="text-[var(--eh-green)]" />
            <span className="eh-mono text-[11px] opacity-80">// MODERATOR MODE · Limited access · Posts you create are subject to owner review</span>
          </div>
        )}

        {allowed('overview') && active==='overview'    && <Overview setActive={setActive} />}
        {allowed('branding') && active==='branding'    && <Branding />}
        {allowed('hero') && active==='hero'        && <HeroEditor />}
        {allowed('navigation') && active==='navigation'  && <NavigationEditor />}
        {allowed('services') && active==='services'    && <ServicesEditor />}
        {allowed('books') && active==='books'       && <BooksEditor />}
        {allowed('memberships') && active==='memberships' && <MembershipsEditor />}
        {allowed('comparison') && active==='comparison'  && <ComparisonEditor />}
        {allowed('tools') && active==='tools'       && <ToolsEditor />}
        {allowed('how') && active==='how'         && <HowEditor />}
        {allowed('partners') && active==='partners'    && <PartnersEditor />}
        {allowed('worksWith') && active==='worksWith'   && <WorksWithManager />}
        {allowed('teamPage') && active==='teamPage'    && <TeamContentManager />}
        {allowed('testimonials') && active==='testimonials'&& <TestimonialsEditor />}
        {allowed('activity') && active==='activity'    && <ActivityEditor />}
        {allowed('stats') && active==='stats'       && <StatsEditor />}
        {allowed('faqs') && active==='faqs'        && <FAQEditor />}
        {allowed('orders') && active==='orders'      && <Orders />}
        {allowed('recovery') && active==='recovery'    && <RecoveryManager />}
        {allowed('infraLeads') && active==='infraLeads' && <InfraLeads />}
        {allowed('users') && active==='users'       && <UsersTab />}
        {allowed('referrals') && active==='referrals'   && <ReferralsTab />}
        {allowed('feed') && active==='feed'        && <FeedManager />}
        {allowed('payments') && active==='payments'    && <PaymentSettingsTab />}
        {allowed('refunds') && active==='refunds'     && <RefundsManager />}
        {allowed('spin') && active==='spin'        && <AdminSpinPanel />}
        {allowed('coupons') && active==='coupons'     && <CouponsTab />}
        {allowed('notifications') && active==='notifications'&& <NotificationsTab />}
        {allowed('announcements') && active==='announcements'&& <AnnouncementsManager />}
        {allowed('team') && active==='team'        && <TeamManager />}
        {allowed('settings') && active==='settings'    && <SettingsTab />}
      </main>
    </div>
  );
};
export default AdminPanel;
